// src/components/Dashboard.jsx
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAdvisor } from "../AdvisorContext";
import "../dashboard.css";
import RecommendationDetailModal from "./compliance/modals/RecommendationDetailModal";
import DismissedRecommendationsModal from "./compliance/modals/DismissedRecommendationsModal";
import AcceptedRecommendationsModal from "./compliance/modals/AcceptedRecommendationsModal";
import RecommendationHandoffPreviewModal from "./compliance/modals/RecommendationHandoffPreviewModal";
import DraftRecommendationHandoffsModal from "./compliance/modals/DraftRecommendationHandoffsModal";
import RecommendationHandoffConvertPreviewModal from "./compliance/modals/RecommendationHandoffConvertPreviewModal";
import ConvertedRecommendationHandoffsModal from "./compliance/modals/ConvertedRecommendationHandoffsModal";
import ContextualExplainDrawer from "./ai/ContextualExplainDrawer";
import ControlsPreviewPanel from "./mission-control/ControlsPreviewPanel";
import { ExecutivePostureMetrics } from "./mission-control/ExecutivePosturePanel";
import {
  ExecutivePosturePanel,
  FrameworkHealthPanel,
  MissionControlHeader,
  MissionControlIntelligencePanel,
  OperationalSignals,
  PriorityAttentionPanel,
} from "./mission-control";


const FRAMEWORK_ORDER = [
  "SOC2",
  "NIST",
  "ISO",
  "FedRAMP",
  "FDA",
  "AML/KYC",
  "CCPA",
  "OSHA",
];

const REQUEST_TIMEOUT_MS = 30000;

const CONNECTOR_PROVIDER_DISPLAY_NAMES = {
  aws: "AWS",
  cloudflare: "Cloudflare",
  github: "GitHub",
  google_workspace: "Google Workspace",
  okta: "Okta",
};

const IDENTITY_INVENTORY_RULE_IDS = new Set([
  "GW_2SV_DISABLED",
  "GW_UNUSED_USER",
  "GW_INACTIVE_ADMIN",
  "GW_ADMIN_COUNT",
  "GW_SUPER_ADMIN_EXPOSURE",
  "OKTA_MFA_DISABLED",
  "OKTA_UNUSED_ACCOUNT",
  "OKTA_STALE_ADMIN",
  "OKTA_ADMIN_EXPOSURE",
  "OKTA_PRIVILEGED_GROUP_EXPOSURE",
]);

const IDENTITY_RULE_LABELS = {
  GW_2SV_DISABLED: "Google Workspace 2SV disabled",
  GW_UNUSED_USER: "Google Workspace unused account",
  GW_INACTIVE_ADMIN: "Google Workspace inactive admin",
  GW_ADMIN_COUNT: "Google Workspace admin count",
  GW_SUPER_ADMIN_EXPOSURE: "Google Workspace super admin exposure",
  OKTA_MFA_DISABLED: "Okta MFA disabled",
  OKTA_UNUSED_ACCOUNT: "Okta unused account",
  OKTA_STALE_ADMIN: "Okta stale admin",
  OKTA_ADMIN_EXPOSURE: "Okta admin exposure",
  OKTA_PRIVILEGED_GROUP_EXPOSURE: "Okta privileged group exposure",
};

const IDENTITY_CATEGORY_RULES = {
  mfa: new Set(["GW_2SV_DISABLED", "OKTA_MFA_DISABLED"]),
  admin: new Set([
    "GW_INACTIVE_ADMIN",
    "GW_ADMIN_COUNT",
    "GW_SUPER_ADMIN_EXPOSURE",
    "OKTA_STALE_ADMIN",
    "OKTA_ADMIN_EXPOSURE",
    "OKTA_PRIVILEGED_GROUP_EXPOSURE",
  ]),
  lifecycle: new Set(["GW_UNUSED_USER", "OKTA_UNUSED_ACCOUNT"]),
};

const CONNECTOR_EVIDENCE_RULE_IDS = new Set([
  "GH_PUBLIC_REPOSITORY",
  "GH_BRANCH_PROTECTION_DISABLED",
  "GH_REQUIRED_REVIEWS_DISABLED",
  "GH_ADMIN_BYPASS_ENABLED",
  "GH_SECRET_SCANNING_DISABLED",
  "GH_DEPENDABOT_DISABLED",
  "CF_WAF_DISABLED",
  "CF_TLS_MIN_VERSION_WEAK",
  "CF_ALWAYS_USE_HTTPS_DISABLED",
  "CF_SECURITY_LEVEL_LOW",
  "CF_DNSSEC_DISABLED",
  "CF_ZONE_ACTIVITY_OBSERVED",
]);

const CONNECTOR_EVIDENCE_RULE_LABELS = {
  GH_PUBLIC_REPOSITORY: "GitHub public repository exposure",
  GH_BRANCH_PROTECTION_DISABLED: "GitHub branch protection unreadable or disabled",
  GH_REQUIRED_REVIEWS_DISABLED: "GitHub required reviews disabled",
  GH_ADMIN_BYPASS_ENABLED: "GitHub admin bypass exposure",
  GH_SECRET_SCANNING_DISABLED: "GitHub secret scanning unavailable",
  GH_DEPENDABOT_DISABLED: "GitHub Dependabot alerts disabled",
  CF_WAF_DISABLED: "Cloudflare WAF disabled",
  CF_TLS_MIN_VERSION_WEAK: "Cloudflare weak TLS minimum",
  CF_ALWAYS_USE_HTTPS_DISABLED: "Cloudflare HTTPS enforcement disabled",
  CF_SECURITY_LEVEL_LOW: "Cloudflare security level low",
  CF_DNSSEC_DISABLED: "Cloudflare DNSSEC unreadable or disabled",
  CF_ZONE_ACTIVITY_OBSERVED: "Cloudflare zone inventory observed",
};

const CONNECTOR_EVIDENCE_CATEGORY_RULES = {
  code: new Set([
    "GH_PUBLIC_REPOSITORY",
    "GH_BRANCH_PROTECTION_DISABLED",
    "GH_REQUIRED_REVIEWS_DISABLED",
    "GH_ADMIN_BYPASS_ENABLED",
    "GH_SECRET_SCANNING_DISABLED",
    "GH_DEPENDABOT_DISABLED",
  ]),
  edge: new Set([
    "CF_WAF_DISABLED",
    "CF_TLS_MIN_VERSION_WEAK",
    "CF_ALWAYS_USE_HTTPS_DISABLED",
    "CF_SECURITY_LEVEL_LOW",
    "CF_DNSSEC_DISABLED",
    "CF_ZONE_ACTIVITY_OBSERVED",
  ]),
  accessLimited: new Set([
    "GH_BRANCH_PROTECTION_DISABLED",
    "GH_REQUIRED_REVIEWS_DISABLED",
    "GH_ADMIN_BYPASS_ENABLED",
    "GH_SECRET_SCANNING_DISABLED",
    "CF_DNSSEC_DISABLED",
  ]),
};

function cleanConnectorProvider(value) {
  return String(value || "").trim().toLowerCase();
}

function formatConnectorProvider(value) {
  const normalizedProvider = cleanConnectorProvider(value);

  if (CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider]) {
    return CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider];
  }

  return String(value || "Unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatIdentityRule(ruleId) {
  const key = String(ruleId || "").trim();
  if (IDENTITY_RULE_LABELS[key]) return IDENTITY_RULE_LABELS[key];

  return key
    ? key.replace(/_/g, " ").replace(/\b\w/g, (match) => match.toUpperCase())
    : "Identity finding";
}

function normalizeFindingState(value) {
  return String(value || "unknown").trim().toLowerCase();
}

function isFailingFinding(finding = {}) {
  const state = normalizeFindingState(finding.state || finding.status);
  return state === "fail" || state === "failed" || state === "error";
}

function getFindingProvider(finding = {}) {
  const source = cleanConnectorProvider(finding.source || finding.provider);
  if (source === "google" || source === "google_workspace") return "google_workspace";
  if (source === "okta") return "okta";
  if (source === "github") return "github";
  if (source === "cloudflare") return "cloudflare";

  const stream = String(finding.stream || "").toLowerCase();
  if (stream.startsWith("google_workspace")) return "google_workspace";
  if (stream.startsWith("okta")) return "okta";
  if (stream.startsWith("github")) return "github";
  if (stream.startsWith("cloudflare")) return "cloudflare";

  const ruleId = String(finding.rule_id || "").trim().toUpperCase();
  if (ruleId.startsWith("GH_")) return "github";
  if (ruleId.startsWith("CF_")) return "cloudflare";

  return source || "identity";
}

function isIdentityInventoryFinding(finding = {}) {
  const ruleId = String(finding.rule_id || "").trim();
  const stream = String(finding.stream || "").trim();

  return (
    IDENTITY_INVENTORY_RULE_IDS.has(ruleId) ||
    stream === "google_workspace_directory" ||
    stream === "okta_identity_inventory"
  );
}


function isConnectorEvidenceFinding(finding = {}) {
  const ruleId = String(finding.rule_id || "").trim();
  const provider = getFindingProvider(finding);
  const stream = String(finding.stream || "").trim().toLowerCase();

  return (
    CONNECTOR_EVIDENCE_RULE_IDS.has(ruleId) ||
    provider === "github" ||
    provider === "cloudflare" ||
    stream === "github_repos" ||
    stream === "cloudflare_default"
  );
}

function formatConnectorEvidenceRule(ruleId) {
  const key = String(ruleId || "").trim();
  if (CONNECTOR_EVIDENCE_RULE_LABELS[key]) return CONNECTOR_EVIDENCE_RULE_LABELS[key];

  return key
    ? key.replace(/_/g, " ").replace(/\b\w/g, (match) => match.toUpperCase())
    : "Connector evidence finding";
}

function formatRelativeTime(value) {
  if (!value) return "—";

  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return "—";

  const diffMs = Date.now() - ts;
  const diffMin = Math.round(diffMs / 60000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;

  const diffDay = Math.round(diffHr / 24);
  return `${diffDay}d ago`;
}

function formatTimestamp(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function formatDurationSeconds(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return "—";

  if (seconds < 60) return `${Math.round(seconds)}s`;

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.round(hours / 24);
  return `${days}d`;
}

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatApiErrorMessage(error) {
  const raw = String(error?.message || error || "").trim();
  if (!raw) return "Request failed.";

  const jsonStart = raw.indexOf("{");
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(jsonStart));
      if (parsed?.detail) return String(parsed.detail);
    } catch (_) {
      // Fall through to text cleanup.
    }
  }

  return raw
    .replace(/^400 Bad Request:\s*/i, "")
    .replace(/^403 Forbidden:\s*/i, "")
    .replace(/^500 Internal Server Error:\s*/i, "")
    .replace(/^verify X-Tenant-Id \/ X-User-Id headers and tenant membership\.\s*/i, "")
    .trim();
}


function formatValidationHealth(value) {
  const normalized = String(value || "unknown").trim().toLowerCase();

  if (normalized === "healthy" || normalized === "passing" || normalized === "clear") {
    return "Healthy";
  }
  if (normalized === "attention_required" || normalized === "attention-required") {
    return "Attention Required";
  }
  if (normalized === "blocked" || normalized === "critical") {
    return "Blocked";
  }
  if (normalized === "pending" || normalized === "in_progress") {
    return "Pending";
  }

  return normalized
    ? normalized.replace(/_/g, " ").replace(/-/g, " ").replace(/\b\w/g, (match) => match.toUpperCase())
    : "Unknown";
}

function validationHealthTone(value, failedResults = 0, pendingRuns = 0) {
  const normalized = String(value || "unknown").trim().toLowerCase();

  if (normalized === "healthy" || normalized === "passing" || normalized === "clear") {
    return "low";
  }
  if (normalized === "blocked" || normalized === "critical") {
    return "high";
  }
  if (normalized === "attention_required" || normalized === "attention-required") {
    return "high";
  }
  if (safeNumber(failedResults) > 0) {
    return "high";
  }
  if (normalized === "pending" || normalized === "in_progress" || safeNumber(pendingRuns) > 0) {
    return "medium";
  }

  return "medium";
}

function clampPercentScore(value) {
  const numeric = Number(value);
  if (value === null || value === undefined || value === "") return null;
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(100, numeric));
}

function coalesceNumber(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === "") continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
}

function formatSignedNumber(value, digits = 2) {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return numeric > 0 ? `+${numeric.toFixed(digits)}` : numeric.toFixed(digits);
}

function estimateOperationalScore({
  rawScore = 0,
  rawFailCount = 0,
  errorCount = 0,
  unknownCount = 0,
  acceptedRiskFindings = 0,
} = {}) {
  const score = clampPercentScore(rawScore);
  if (score === null) return null;
  const accepted = Math.max(0, safeNumber(acceptedRiskFindings, 0));
  const burden = Math.max(
    0,
    safeNumber(rawFailCount, 0) + safeNumber(errorCount, 0) + safeNumber(unknownCount, 0)
  );

  if (accepted <= 0 || burden <= 0 || score >= 100) return score;

  const acceptedShare = Math.min(1, accepted / burden);
  const remainingGap = Math.max(0, 100 - score);
  return clampPercentScore(score + remainingGap * acceptedShare);
}

function normalizeSopPendingItems(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.sops)) return value.sops;
  if (Array.isArray(value?.pending_approval)) return value.pending_approval;
  if (Array.isArray(value?.pending_versions)) return value.pending_versions;
  if (Array.isArray(value?.versions)) return value.versions;
  return [];
}

function getSopPendingCount(value) {
  if (value && typeof value === "object") {
    const explicit =
      value.total ??
      value.total_count ??
      value.count ??
      value.pending_count ??
      value.pending_approval_count ??
      value.pending_versions_count;

    if (explicit !== undefined && explicit !== null) {
      return safeNumber(explicit, 0);
    }
  }

  return normalizeSopPendingItems(value).length;
}

function normalizeSopLibraryRows(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.sops)) return value.sops;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

function getSopPendingCountFromLibrary(value) {
  const rows = normalizeSopLibraryRows(value);

  return rows.reduce((total, row) => {
    const rowStatus = String(row?.status || "").trim().toLowerCase();
    const latestVersionStatus = String(
      row?.latest_version_status || row?.version_status || ""
    )
      .trim()
      .toLowerCase();

    const rowPending =
      rowStatus === "pending approval" ||
      rowStatus === "pending_approval" ||
      latestVersionStatus === "pending approval" ||
      latestVersionStatus === "pending_approval";

    const pendingVersions = safeNumber(row?.pending_versions_count, 0);

    return total + (rowPending ? 1 : 0) + pendingVersions;
  }, 0);
}

function normalizeSopOverdueItems(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.sops)) return value.sops;
  if (Array.isArray(value?.overdue)) return value.overdue;
  if (Array.isArray(value?.outdated)) return value.outdated;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

function getSopOverdueCount(value) {
  if (value && typeof value === "object") {
    const explicit =
      value.total ??
      value.total_count ??
      value.count ??
      value.overdue_count ??
      value.outdated_count;

    if (explicit !== undefined && explicit !== null) {
      return safeNumber(explicit, 0);
    }
  }

  return normalizeSopOverdueItems(value).length;
}

function normalizeRemediationTasks(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.tasks)) return value.tasks;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
}

function isClosedRemediationTask(task = {}) {
  const status = String(task?.status || task?.task_status || task?.state || "")
    .trim()
    .toLowerCase();
  return status === "closed" || status === "complete" || status === "completed";
}


function formatRecommendationPriority(value) {
  const normalized = String(value || "medium").trim().toLowerCase();
  if (normalized === "critical") return "Critical";
  if (normalized === "high") return "High";
  if (normalized === "low") return "Low";
  return "Medium";
}

function recommendationPriorityTone(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "critical" || normalized === "high") return "high";
  if (normalized === "medium") return "medium";
  return "low";
}

function getRecommendationNavigationTarget(recommendation = {}, fallbackFramework = undefined) {
  const category = String(recommendation.category || recommendation.recommendation_type || "")
    .trim()
    .toLowerCase();
  const framework = recommendation.affected_framework || fallbackFramework || undefined;

  if (category === "validation") {
    return { mode: "validation", params: { framework, section: "failed" } };
  }

  if (category === "connector_health") {
    return { mode: "settings", params: { settingsSection: "connectors" } };
  }

  if (category === "workflow") {
    return { mode: "workflows", params: { section: "workflow_health" } };
  }

  if (category === "sop") {
    return { mode: "sop", params: { filter: "recommendations" } };
  }

  if (category === "freshness") {
    return { mode: "posture", params: { framework, section: "stale" } };
  }

  return { mode: "posture", params: { framework, section: "top_risks" } };
}

function MetricCard({ label, value, footnote, tone = "default", onClick, title }) {
  const toneStyle =
    tone === "danger"
      ? { color: "var(--danger)" }
      : tone === "warn"
      ? { color: "var(--warn)" }
      : tone === "ok"
      ? { color: "var(--ok)" }
      : { color: "#E8EEF8" };

  const content = (
    <>
      <div className="kpi-label" style={{ color: "#E8EEF8" }}>
        {label}
      </div>
      <div className="kpi-value small" style={toneStyle}>
        {value}
      </div>
      {footnote ? (
        <div className="hero-kpi-foot" style={{ color: "#A8B3C7" }}>
          {footnote}
        </div>
      ) : null}
    </>
  );

  const baseStyle = {
    color: "#E8EEF8",
  };

  if (typeof onClick === "function") {
    return (
      <button
        type="button"
        className="hero-kpi"
        onClick={onClick}
        title={title}
        style={{
          ...baseStyle,
          appearance: "none",
          border: 0,
          textAlign: "left",
          cursor: "pointer",
          background: "transparent",
          padding: 0,
        }}
      >
        {content}
      </button>
    );
  }

  return (
    <div className="hero-kpi" style={baseStyle}>
      {content}
    </div>
  );
}

function StatusPill({ value }) {
  const status = String(value || "").toLowerCase();
  const style =
    status === "resolved"
      ? {
          background: "color-mix(in srgb, var(--ok), transparent 88%)",
          color: "var(--ok)",
        }
      : status === "acknowledged"
      ? {
          background: "color-mix(in srgb, var(--brand), transparent 88%)",
          color: "var(--brand)",
        }
      : status === "snoozed"
      ? {
          background: "color-mix(in srgb, var(--warn), transparent 88%)",
          color: "var(--warn)",
        }
      : {
          background: "color-mix(in srgb, var(--danger), transparent 88%)",
          color: "var(--danger)",
        };

  return (
    <span
      style={{
        ...style,
        borderRadius: 999,
        padding: "4px 9px",
        fontSize: 11,
        fontWeight: 700,
        textTransform: "capitalize",
      }}
    >
      {status || "open"}
    </span>
  );
}

function SeverityPill({ value }) {
  const sev = String(value || "").toLowerCase();
  const style =
    sev === "critical"
      ? {
          background: "color-mix(in srgb, var(--danger), transparent 88%)",
          color: "var(--danger)",
        }
      : sev === "high"
      ? {
          background: "color-mix(in srgb, var(--warn), transparent 88%)",
          color: "var(--warn)",
        }
      : sev === "medium"
      ? {
          background: "color-mix(in srgb, var(--brand), transparent 88%)",
          color: "var(--brand)",
        }
      : {
          background: "color-mix(in srgb, var(--ok), transparent 88%)",
          color: "var(--ok)",
        };

  return (
    <span
      style={{
        ...style,
        borderRadius: 999,
        padding: "4px 9px",
        fontSize: 11,
        fontWeight: 700,
        textTransform: "capitalize",
      }}
    >
      {sev || "info"}
    </span>
  );
}


export default function Dashboard({
  apiBaseUrl,
  getAccessToken,
  onModeChange,
  themeMode = "light",
  userProfile = null,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const { setCopilotContext, clearCopilotContext } = useAdvisor();

  const [frameworkFilter, setFrameworkFilter] = useState(null);
  const [frameworkScope, setFrameworkScope] = useState(null);
  const [controlsPreview, setControlsPreview] = useState({ rows: [], loading: false, error: "" });
  const [alertsLimit, setAlertsLimit] = useState(5);
  const [showScoreDetailsModal, setShowScoreDetailsModal] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const [refreshingView, setRefreshingView] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState("");
  const [refreshError, setRefreshError] = useState("");
  const manualRefreshRef = useRef(false);
  const priorPostureTimestampRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [panelWarnings, setPanelWarnings] = useState([]);

  const [overview, setOverview] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState([]);
  const [topRisks, setTopRisks] = useState([]);
  const [staleControls, setStaleControls] = useState([]);
  const [postureDelta, setPostureDelta] = useState([]);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [alertsSummary, setAlertsSummary] = useState(null);
  const [sopPendingApproval, setSopPendingApproval] = useState({
    count: 0,
    items: [],
    loaded: false,
  });
  const [sopOverdue, setSopOverdue] = useState({
    count: 0,
    items: [],
    loaded: false,
  });
  const [remediationTasks, setRemediationTasks] = useState([]);
  const [connectorHealth, setConnectorHealth] = useState(null);
  const [workflowSummary, setWorkflowSummary] = useState(null);
  const [validationDashboard, setValidationDashboard] = useState(null);
  const [priorityActionQueue, setPriorityActionQueue] = useState(null);
  const [identityFindings, setIdentityFindings] = useState([]);
  const [recommendationSummary, setRecommendationSummary] = useState(null);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [recommendationError, setRecommendationError] = useState(null);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);

  const [missionControlIntelligence, setMissionControlIntelligence] = useState({
    question: "What needs attention now?",
    card: null,
    metadata: null,
    loading: false,
    error: "",
  });

  const [contextualExplain, setContextualExplain] = useState({
    open: false,
    recordType: "risk",
    record: null,
    question: "Explain this risk.",
    card: null,
    metadata: null,
    loading: false,
    error: "",
  });
  const [recommendationFeedbackState, setRecommendationFeedbackState] = useState({
    loadingType: null,
    message: "",
    error: "",
  });
  const [showDismissedRecommendationsModal, setShowDismissedRecommendationsModal] = useState(false);
  const [dismissedRecommendations, setDismissedRecommendations] = useState({
    items: [],
    count: 0,
    loaded: false,
  });
  const [dismissedRecommendationsLoading, setDismissedRecommendationsLoading] = useState(false);
  const [dismissedRecommendationsError, setDismissedRecommendationsError] = useState("");
  const [showAcceptedRecommendationsModal, setShowAcceptedRecommendationsModal] = useState(false);
  const [acceptedRecommendations, setAcceptedRecommendations] = useState({
    items: [],
    count: 0,
    loaded: false,
  });
  const [acceptedRecommendationsLoading, setAcceptedRecommendationsLoading] = useState(false);
  const [acceptedRecommendationsError, setAcceptedRecommendationsError] = useState("");
  const [restoringRecommendationId, setRestoringRecommendationId] = useState(null);
  const [dismissedRecommendationsMessage, setDismissedRecommendationsMessage] = useState("");
  const [acceptedRecommendationsMessage, setAcceptedRecommendationsMessage] = useState("");
  const [showRecommendationHandoffPreviewModal, setShowRecommendationHandoffPreviewModal] = useState(false);
  const [recommendationHandoffPreview, setRecommendationHandoffPreview] = useState(null);
  const [recommendationHandoffPreviewLoading, setRecommendationHandoffPreviewLoading] = useState(false);
  const [recommendationHandoffPreviewError, setRecommendationHandoffPreviewError] = useState("");
  const [preparingHandoffRecommendationId, setPreparingHandoffRecommendationId] = useState(null);
  const [creatingDraftHandoff, setCreatingDraftHandoff] = useState(false);
  const [draftHandoffResult, setDraftHandoffResult] = useState(null);
  const [draftHandoffError, setDraftHandoffError] = useState("");
  const [showDraftHandoffsModal, setShowDraftHandoffsModal] = useState(false);
  const [draftHandoffs, setDraftHandoffs] = useState({
    items: [],
    count: 0,
    loaded: false,
  });
  const [draftHandoffsLoading, setDraftHandoffsLoading] = useState(false);
  const [draftHandoffsError, setDraftHandoffsError] = useState("");
  const [draftHandoffsMessage, setDraftHandoffsMessage] = useState("");
  const [cancellingDraftHandoffId, setCancellingDraftHandoffId] = useState(null);
  const [showConvertedHandoffsModal, setShowConvertedHandoffsModal] = useState(false);
  // R23: read-only shared SOC 2 evidence state; never replace the legacy score.
  const [sharedAwsAssessment, setSharedAwsAssessment] = useState(null);
  const [sharedAwsAssessmentError, setSharedAwsAssessmentError] = useState("");
  // Original normalized findings, separate from framework assurance.
  const [operationalRiskQueue, setOperationalRiskQueue] = useState(null);
  const [operationalRiskQueueError, setOperationalRiskQueueError] = useState("");

  const [convertedHandoffs, setConvertedHandoffs] = useState({
    items: [],
    count: 0,
    loaded: false,
  });
  const [convertedHandoffsLoading, setConvertedHandoffsLoading] = useState(false);
  const [convertedHandoffsError, setConvertedHandoffsError] = useState("");
  const [convertedHandoffsMessage, setConvertedHandoffsMessage] = useState("");
  const [showHandoffConvertPreviewModal, setShowHandoffConvertPreviewModal] = useState(false);
  const [handoffConvertPreview, setHandoffConvertPreview] = useState(null);
  const [handoffConvertPreviewLoading, setHandoffConvertPreviewLoading] = useState(false);
  const [handoffConvertPreviewError, setHandoffConvertPreviewError] = useState("");
  const [previewingConvertHandoffId, setPreviewingConvertHandoffId] = useState(null);
  const [confirmingHandoffConversion, setConfirmingHandoffConversion] = useState(false);
  const [handoffConversionResult, setHandoffConversionResult] = useState(null);
  const [handoffConversionError, setHandoffConversionError] = useState("");

  const scrollMissionControlToTop = useCallback(() => {
    if (typeof window === "undefined") return;

    const reset = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    };

    reset();
    window.requestAnimationFrame(reset);
    window.setTimeout(reset, 0);
    window.setTimeout(reset, 80);
    window.setTimeout(reset, 180);
  }, []);

  useLayoutEffect(() => {
    scrollMissionControlToTop();
  }, [scrollMissionControlToTop]);

  useEffect(() => {
    if (!loading) {
      scrollMissionControlToTop();
    }
  }, [loading, scrollMissionControlToTop]);

  const normalizedBase = useMemo(() => {
    const base = (apiBaseUrl || "").trim();
    return base.endsWith("/") ? base.slice(0, -1) : base;
  }, [apiBaseUrl]);

  const tenantId =
    userProfile?.tenant_id ||
    userProfile?.tenantId ||
    process.env.REACT_APP_TENANT_ID ||
    "";

  const userId =
    userProfile?.user_id ||
    userProfile?.userId ||
    userProfile?.id ||
    userProfile?.sub ||
    "";

  const fetchToken = useCallback(async () => {
    if (typeof getAccessToken === "function") {
      return await getAccessToken();
    }

    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    return await getAccessTokenSilently(
      audience ? { authorizationParams: { audience } } : undefined
    );
  }, [getAccessToken, getAccessTokenSilently]);

  const getJsonWithToken = useCallback(
    async (token, path, params = {}, signal) => {
      const searchParams = new URLSearchParams();

      Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      const url = searchParams.toString()
        ? `${normalizedBase}${path}?${searchParams.toString()}`
        : `${normalizedBase}${path}`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const res = await fetch(url, {
        method: "GET",
        headers,
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        if (res.status === 401) {
          throw new Error("401 Unauthorized: Missing or invalid bearer token.");
        }
        if (res.status === 400 || res.status === 403) {
          throw new Error(
            `${res.status} ${res.statusText}: verify X-Tenant-Id / X-User-Id headers and tenant membership. ${text}`
          );
        }
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json();
    },
    [normalizedBase, tenantId, userId]
  );


  // DEV86: Read the original mapped finding queue, never a new risk engine.
  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    setOperationalRiskQueue(null);
    setOperationalRiskQueueError("");

    if (!tenantId || !userId || frameworkFilter !== "SOC2") {
      return () => {
        active = false;
        controller.abort();
      };
    }

    (async () => {
      try {
        const token = await fetchToken();
        if (!active) return;

        const result = await getJsonWithToken(
          token,
          "/dashboard/risk-queue",
          { framework: "SOC2", limit: 25 },
          controller.signal
        );

        if (!active) return;
        if (!Array.isArray(result?.controls)) {
          throw new Error("Unexpected operational risk queue response");
        }

        setOperationalRiskQueue(result.controls);
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        setOperationalRiskQueueError(
          "Operational findings are unavailable. No finding status is inferred."
        );
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    tenantId,
    userId,
    frameworkFilter,
    fetchToken,
    getJsonWithToken,
    refreshNonce,
  ]);

  // R23: scoped, authenticated read-only evidence status, refreshed with Mission Control.
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setSharedAwsAssessment(null);
    setSharedAwsAssessmentError("");
    if (!tenantId || !userId || frameworkFilter !== "SOC2") {
      return () => { active = false; controller.abort(); };
    }
    (async () => {
      try {
        const token = await fetchToken();
        if (!active) return;
        const response = await getJsonWithToken(token, "/dashboard/aws-assessment-status", {}, controller.signal);
        if (!active) return;
        if (response?.framework !== "SOC2" || response?.framework_score_publishable !== false || response?.assessment_score != null || !Array.isArray(response?.sources)) {
          throw new Error("Unexpected assessment status contract");
        }
        setSharedAwsAssessment(response);
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        setSharedAwsAssessmentError("Verified AWS assessment status is unavailable; no new SOC 2 result can be confirmed.");
      }
    })();
    return () => { active = false; controller.abort(); };
  }, [tenantId, userId, frameworkFilter, fetchToken, getJsonWithToken, refreshNonce]);

  // Framework identity is governed by the organization's persisted authority,
  // never inferred from a dashboard's historical framework data.
  useEffect(() => {
    let active = true;
    if (!tenantId || !userId) return undefined;
    (async () => {
      try {
        const token = await fetchToken();
        const result = await getJsonWithToken(token, "/dashboard/applied-frameworks");
        if (!active) return;
        const frameworks = Array.isArray(result?.frameworks) ? result.frameworks : [];
        if (!frameworks.length) throw new Error("No applied frameworks are configured for this organization.");
        setFrameworkScope({ tenantId, frameworks });
        setFrameworkFilter((previous) => frameworks.includes(previous) ? previous : frameworks[0]);
      } catch (e) {
        if (!active) return;
        setFrameworkScope(null);
        setErr(`Framework Authority: ${String(e?.message || e)}`);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [fetchToken, getJsonWithToken, tenantId, userId]);

  // Read-only preview of authoritative per-control state. This existing API
  // does not supply control names, implementation %, evidence counts, or formal
  // review timestamps. Those columns deliberately remain unavailable.
  useEffect(() => {
    if (!tenantId || !userId || !frameworkFilter ||
        frameworkScope?.tenantId !== tenantId ||
        !frameworkScope.frameworks.includes(frameworkFilter)) {
      setControlsPreview({ rows: [], loading: false, error: "" });
      return undefined;
    }
    const controller = new AbortController();
    let active = true;
    setControlsPreview({ rows: [], loading: true, error: "" });
    (async () => {
      try {
        const token = await fetchToken();
        if (controller.signal.aborted) return;
        const payload = await getJsonWithToken(token, "/dashboard/control-status/latest", {
          framework: frameworkFilter,
          limit: 7,
          offset: 0,
        }, controller.signal);
        if (!active || controller.signal.aborted) return;
        setControlsPreview({
          rows: Array.isArray(payload?.controls) ? payload.controls.filter(
            (row) => row.framework === frameworkFilter
          ).slice(0, 7) : [],
          loading: false,
          error: "",
        });
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        setControlsPreview({ rows: [], loading: false, error: String(error?.message || error) });
      }
    })();
    return () => { active = false; controller.abort(); };
  }, [tenantId, userId, frameworkFilter, frameworkScope, fetchToken, getJsonWithToken, refreshNonce]);

  const postJsonWithToken = useCallback(
    async (token, path, body = {}, signal) => {
      const url = `${normalizedBase}${path}`;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      if (tenantId) headers["X-Tenant-Id"] = tenantId;
      if (userId) headers["X-User-Id"] = userId;

      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body || {}),
        signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        if (res.status === 401) {
          throw new Error("401 Unauthorized: Missing or invalid bearer token.");
        }
        if (res.status === 400 || res.status === 403) {
          throw new Error(
            `${res.status} ${res.statusText}: verify X-Tenant-Id / X-User-Id headers and tenant membership. ${text}`
          );
        }
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
      }

      return res.json();
    },
    [normalizedBase, tenantId, userId]
  );

  useEffect(() => {
    let mounted = true;
    const activeControllers = new Set();
    const isManualRefresh = manualRefreshRef.current;

    if (isManualRefresh) {
      setRefreshingView(true);
      setRefreshMessage("");
      setRefreshError("");
    } else {
      setLoading(true);
      setErr(null);
      setPanelWarnings([]);
      setRecommendationSummary(null);
      setRecommendationError(null);
      setRecommendationLoading(false);
      setRecommendationFeedbackState({ loadingType: null, message: "", error: "" });
      setDismissedRecommendationsMessage("");
      setAcceptedRecommendationsMessage("");
      setRecommendationHandoffPreview(null);
      setRecommendationHandoffPreviewError("");
      setRecommendationHandoffPreviewLoading(false);
      setPreparingHandoffRecommendationId(null);
      setCreatingDraftHandoff(false);
      setDraftHandoffResult(null);
      setDraftHandoffError("");
      setDraftHandoffsMessage("");
      setDraftHandoffsError("");
      setCancellingDraftHandoffId(null);
      setConvertedHandoffsMessage("");
      setConvertedHandoffsError("");
      setHandoffConvertPreview(null);
      setHandoffConvertPreviewError("");
      setHandoffConvertPreviewLoading(false);
      setPreviewingConvertHandoffId(null);
      setConfirmingHandoffConversion(false);
      setHandoffConversionResult(null);
      setHandoffConversionError("");
    }

    const runPanelRequest = async (request) => {
      const controller = new AbortController();
      activeControllers.add(controller);

      const timeoutId = setTimeout(() => {
        controller.abort(`${request.label || "Request"} timed out after ${REQUEST_TIMEOUT_MS / 1000}s`);
      }, REQUEST_TIMEOUT_MS);

      try {
        return await request.load(controller.signal);
      } finally {
        clearTimeout(timeoutId);
        activeControllers.delete(controller);
      }
    };

    if (!frameworkScope || frameworkScope.tenantId !== tenantId || !frameworkScope.frameworks.includes(frameworkFilter)) {
      return () => { mounted = false; };
    }

    (async () => {
      try {
        if (!tenantId || !userId) {
          throw new Error(
            "Mission Control requires tenant and user identifiers. Verify userProfile contains tenant_id and a UUID user_id."
          );
        }

        const token = await fetchToken();
        const frameworkParam = frameworkFilter;
        // Keep the executive posture panel scoped to one framework by default.
        // The all-framework overview endpoint can be expensive locally and has caused browser timeouts.
        const selectedFrameworkParam = frameworkFilter;

        const requests = [
          {
            key: "overview",
            required: true,
            label: "Overview",
            load: (signal) =>
              getJsonWithToken(
                token,
                "/dashboard/posture/overview",
                selectedFrameworkParam ? { framework: selectedFrameworkParam } : {},
                signal
              ),
          },
          {
            key: "summary",
            required: true,
            label: "Framework summary",
            load: (signal) => getJsonWithToken(token, "/dashboard/summary", {}, signal),
          },
          {
            key: "topRisks",
            required: false,
            label: "Top risks",
            load: (signal) =>
              getJsonWithToken(
                token,
                "/dashboard/top-risks",
                selectedFrameworkParam ? { framework: selectedFrameworkParam, limit: 5 } : { limit: 5 },
                signal
              ),
          },
          {
            key: "staleControls",
            required: false,
            label: "Stale controls",
            load: (signal) =>
              getJsonWithToken(
                token,
                "/dashboard/stale-controls",
                selectedFrameworkParam ? { framework: selectedFrameworkParam, limit: 6 } : { limit: 6 },
                signal
              ),
          },
          {
            key: "postureDelta",
            required: true,
            label: "Posture delta",
            load: (signal) =>
              getJsonWithToken(
                token,
                "/dashboard/posture/delta",
                selectedFrameworkParam ? { framework: selectedFrameworkParam } : {},
                signal
              ),
          },
          {
            key: "recentAlerts",
            required: false,
            label: "Recent alerts",
            load: (signal) =>
              getJsonWithToken(token, "/alerts/recent", { limit: alertsLimit }, signal),
          },
          {
            key: "alertsSummary",
            required: true,
            label: "Alerts summary",
            load: (signal) => getJsonWithToken(token, "/alerts/summary", {}, signal),
          },
          {
            key: "sopPendingApproval",
            required: false,
            label: "SOP pending approvals",
            load: (signal) =>
              getJsonWithToken(token, "/sops/pending-approval", { limit: 250 }, signal),
          },
          {
            key: "sopOverdue",
            required: false,
            label: "SOP overdue",
            load: (signal) =>
              getJsonWithToken(
                token,
                "/sops/overdue",
                { refresh: true, limit: 250 },
                signal
              ),
          },
          {
            key: "sopLibrary",
            required: false,
            label: "SOP library fallback",
            load: (signal) =>
              getJsonWithToken(
                token,
                "/sops/library",
                { limit: 250, tenant_id: tenantId },
                signal
              ),
          },
          {
            key: "remediationTasks",
            required: false,
            label: "Remediation tasks",
            load: (signal) =>
              getJsonWithToken(
                token,
                "/remediation/tasks",
                {
                  framework: frameworkParam || undefined,
                  include_closed: true,
                  limit: 200,
                },
                signal
              ),
          },
          {
            key: "connectorHealth",
            required: false,
            label: "Connector health",
            load: (signal) =>
              getJsonWithToken(token, "/connectors/operational-health", {}, signal),
          },
          {
            key: "workflowSummary",
            required: false,
            label: "Workflow summary",
            load: (signal) =>
              getJsonWithToken(
                token,
                "/workflows/summary",
                tenantId ? { tenant_id: tenantId } : {},
                signal
              ),
          },
          {
            key: "validationDashboard",
            required: false,
            label: "Validation dashboard",
            load: (signal) =>
              getJsonWithToken(
                token,
                "/validation/dashboard",
                frameworkParam ? { framework: frameworkParam } : {},
                signal
              ),
          },
          {
            key: "priorityActionQueue",
            required: false,
            label: "Priority action authority",
            load: (signal) =>
              getJsonWithToken(
                token,
                "/dashboard/priority-actions",
                frameworkParam ? { framework: frameworkParam } : {},
                signal
              ),
          },
          {
            key: "identityFindings",
            required: false,
            label: "Identity findings",
            load: (signal) =>
              getJsonWithToken(token, "/normalize/findings", { limit: 200 }, signal),
          },
        ];

        const settled = await Promise.allSettled(
          requests.map((item) => runPanelRequest(item))
        );
        if (!mounted) return;

        const nextWarnings = [];
        const nextData = {
          overview: null,
          summary: [],
          topRisks: [],
          staleControls: [],
          postureDelta: [],
          recentAlerts: [],
          alertsSummary: null,
          sopPendingApproval: {
            count: 0,
            items: [],
            loaded: false,
          },
          sopOverdue: {
            count: 0,
            items: [],
            loaded: false,
          },
          sopLibraryFallback: {
            count: 0,
            loaded: false,
          },
          remediationTasks: [],
          connectorHealth: null,
          workflowSummary: null,
          validationDashboard: null,
          priorityActionQueue: null,
          identityFindings: [],
        };

        settled.forEach((result, index) => {
          const request = requests[index];

          if (result.status === "fulfilled") {
            const value = result.value;

            if (request.key === "overview") {
              nextData.overview = value || null;
            } else if (request.key === "summary") {
              nextData.summary = Array.isArray(value?.frameworks) ? value.frameworks : [];
            } else if (request.key === "topRisks") {
              nextData.topRisks = Array.isArray(value?.controls) ? value.controls : [];
            } else if (request.key === "staleControls") {
              nextData.staleControls = Array.isArray(value?.controls) ? value.controls : [];
            } else if (request.key === "postureDelta") {
              nextData.postureDelta = Array.isArray(value?.frameworks) ? value.frameworks : [];
            } else if (request.key === "recentAlerts") {
              nextData.recentAlerts = Array.isArray(value?.alerts) ? value.alerts : [];
            } else if (request.key === "alertsSummary") {
              nextData.alertsSummary = value || null;
            } else if (request.key === "sopPendingApproval") {
              nextData.sopPendingApproval = {
                count: getSopPendingCount(value),
                items: normalizeSopPendingItems(value),
                loaded: true,
              };
            } else if (request.key === "sopOverdue") {
              nextData.sopOverdue = {
                count: getSopOverdueCount(value),
                items: normalizeSopOverdueItems(value),
                loaded: true,
              };
            } else if (request.key === "sopLibrary") {
              nextData.sopLibraryFallback = {
                count: getSopPendingCountFromLibrary(value),
                loaded: true,
              };
            }
            else if (request.key === "remediationTasks") {
              nextData.remediationTasks = normalizeRemediationTasks(value);
            } else if (request.key === "connectorHealth") {
              nextData.connectorHealth = value || null;
            } else if (request.key === "workflowSummary") {
              nextData.workflowSummary = value || null;
            } else if (request.key === "validationDashboard") {
              nextData.validationDashboard = value || null;
            } else if (request.key === "priorityActionQueue") {
              nextData.priorityActionQueue = value || null;
            } else if (request.key === "identityFindings") {
              nextData.identityFindings = Array.isArray(value?.findings) ? value.findings : [];
            }

            return;
          }

          const reason = result.reason;
          const abortReason = reason?.name === "AbortError" ? reason?.message || reason : null;
          const message = abortReason
            ? `${request.label} timed out after ${REQUEST_TIMEOUT_MS / 1000}s.`
            : String(reason?.message || reason || `${request.label} failed.`);

          nextWarnings.push(`${request.label} unavailable: ${message}`);
        });

        const hasPrimaryData =
          Boolean(nextData.overview) ||
          nextData.summary.length > 0 ||
          nextData.postureDelta.length > 0 ||
          Boolean(nextData.alertsSummary);

        if (!hasPrimaryData) {
          throw new Error(
            nextWarnings.length
              ? `Mission Control failed to load primary data. ${nextWarnings[0]}`
              : "Mission Control failed to load primary data."
          );
        }

        setOverview(nextData.overview);
        setDashboardSummary(nextData.summary);
        setTopRisks(nextData.topRisks);
        setStaleControls(nextData.staleControls);
        setPostureDelta(nextData.postureDelta);
        const pendingEndpointCount = safeNumber(nextData.sopPendingApproval.count, 0);
        const libraryFallbackCount = safeNumber(nextData.sopLibraryFallback.count, 0);

        setRecentAlerts(nextData.recentAlerts);
        setAlertsSummary(nextData.alertsSummary);
        setSopPendingApproval({
          count: Math.max(pendingEndpointCount, libraryFallbackCount),
          items: nextData.sopPendingApproval.items,
          loaded:
            nextData.sopPendingApproval.loaded || nextData.sopLibraryFallback.loaded,
        });
        setSopOverdue(nextData.sopOverdue);
        setRemediationTasks(nextData.remediationTasks);
        setConnectorHealth(nextData.connectorHealth);
        setWorkflowSummary(nextData.workflowSummary);
        setValidationDashboard(nextData.validationDashboard);
        setPriorityActionQueue(nextData.priorityActionQueue);
        setIdentityFindings(nextData.identityFindings);
        setPanelWarnings(nextWarnings);

        if (isManualRefresh) {
          const nextPostureTimestamp =
            nextData.overview?.data_as_of ||
            nextData.overview?.score_captured_at ||
            nextData.overview?.freshness_captured_at ||
            nextData.overview?.at ||
            null;
          const priorPostureTimestamp = priorPostureTimestampRef.current;

          setRefreshMessage(
            nextPostureTimestamp &&
              priorPostureTimestamp &&
              String(nextPostureTimestamp) !== String(priorPostureTimestamp)
              ? "View refreshed with newer posture data."
              : "View refreshed. No newer posture snapshot is available yet."
          );
        }
      } catch (e) {
        if (!mounted) return;
        console.error("Mission Control load error:", e);

        if (isManualRefresh) {
          setRefreshError(
            `Refresh failed: ${formatApiErrorMessage(e)}`
          );
        } else {
          setErr(String(e?.message || e));
        }
      } finally {
        if (mounted) {
          if (isManualRefresh) {
            setRefreshingView(false);
            manualRefreshRef.current = false;
          } else {
            setLoading(false);
          }
        }
      }
    })();

    return () => {
      mounted = false;
      activeControllers.forEach((controller) => controller.abort("mission-control-unmounted"));
      activeControllers.clear();
    };
  }, [
    fetchToken,
    getJsonWithToken,
    frameworkScope,
    frameworkFilter,
    alertsLimit,
    tenantId,
    userId,
    refreshNonce,
  ]);

  const deltaMap = useMemo(() => {
    const map = {};
    postureDelta.forEach((item) => {
      map[item.framework] = item;
    });
    return map;
  }, [postureDelta]);

  const frameworkRows = useMemo(() => {
    const rows = [...dashboardSummary].map((item) => {
      const delta = deltaMap[item.framework] || {};
      const scoreCounts = item?.score_counts || {};
      const rawScore = clampPercentScore(
        coalesceNumber(
          item?.raw_latest_score,
          item?.latest_score,
          delta?.raw_latest_score,
          delta?.latest_score
        )
      );

      const rawFailFindings = safeNumber(
        item?.raw_fail_findings,
        safeNumber(scoreCounts.fail_count)
      );
      const errorCount = safeNumber(scoreCounts.error_count);
      const unknownCount = safeNumber(scoreCounts.unknown_count);
      const rawNonpassingFindings = safeNumber(
        item?.raw_nonpassing_findings,
        rawFailFindings + errorCount + unknownCount
      );
      const acceptedRiskFindings = safeNumber(
        coalesceNumber(
          item?.accepted_risk_findings,
          delta?.accepted_risk_findings,
          0
        ),
        0
      );
      const activeFindings = safeNumber(
        coalesceNumber(
          item?.active_fail_findings,
          delta?.active_fail_findings,
          Math.max(0, rawFailFindings - acceptedRiskFindings)
        ),
        0
      );
      const operationalScore = clampPercentScore(
        coalesceNumber(
          item?.operational_score,
          delta?.operational_score,
          estimateOperationalScore({
            rawScore,
            rawFailCount: rawFailFindings,
            errorCount,
            unknownCount,
            acceptedRiskFindings,
          })
        )
      );

      return {
        framework: item.framework,
        rawScore,
        operationalScore,
        latestScore: operationalScore,
        staleCount: safeNumber(item?.freshness?.stale, item?.stale_controls || 0),
        activeFindings,
        failingControlCount: activeFindings,
        rawFailFindings,
        rawNonpassingFindings,
        acceptedRiskFindings,
        delta: safeNumber(delta?.operational_score_delta ?? delta?.delta, 0),
        latestScoreCapturedAt: item.latest_score_captured_at,
      };
    });

    const authorized = (frameworkScope?.tenantId === tenantId ? frameworkScope.frameworks : []);
    const scopedRows = rows.filter((r) => authorized.includes(r.framework));
    const known = FRAMEWORK_ORDER.map((fw) => scopedRows.find((r) => r.framework === fw)).filter(Boolean);
    const remaining = scopedRows
      .filter((r) => !FRAMEWORK_ORDER.includes(r.framework))
      .sort((a, b) => b.activeFindings - a.activeFindings);

    return [...known, ...remaining];
  }, [dashboardSummary, deltaMap, frameworkScope, tenantId]);

  const displayedFrameworkRows = useMemo(() => frameworkRows.slice(0, 7), [frameworkRows]);

  const closedRemediationCount = useMemo(() => {
    return remediationTasks.filter((task) => isClosedRemediationTask(task)).length;
  }, [remediationTasks]);

  const heroMetrics = useMemo(() => {
    const selectedDelta = postureDelta.find((item) => item.framework === overview?.framework) || null;

    const rawScore = clampPercentScore(
      coalesceNumber(
        overview?.raw_latest_score,
        overview?.latest_score,
        selectedDelta?.raw_latest_score,
        selectedDelta?.latest_score
      )
    );

    const scoreCounts = overview?.score_counts || {};
    const rawFailCount = safeNumber(
      overview?.raw_fail_findings,
      safeNumber(scoreCounts.fail_count)
    );
    const errorCount = safeNumber(scoreCounts.error_count);
    const unknownCount = safeNumber(scoreCounts.unknown_count);
    const rawNonpassingFindings = safeNumber(
      overview?.raw_nonpassing_findings,
      rawFailCount + errorCount + unknownCount
    );
    const acceptedRiskFindings = safeNumber(overview?.accepted_risk_findings, 0);
    const activeFindings = safeNumber(
      overview?.active_fail_findings,
      Math.max(0, rawFailCount - acceptedRiskFindings)
    );

    const operationalScore = clampPercentScore(
      coalesceNumber(
        overview?.operational_score,
        selectedDelta?.operational_score,
        estimateOperationalScore({
          rawScore,
          rawFailCount,
          errorCount,
          unknownCount,
          acceptedRiskFindings,
        })
      )
    );

    const previousRawScore = coalesceNumber(
      overview?.previous_raw_score,
      selectedDelta?.raw_previous_score,
      selectedDelta?.previous_score
    );
    const previousOperationalScore = coalesceNumber(
      overview?.previous_operational_score,
      selectedDelta?.previous_operational_score
    );

    const rawScoreDelta = coalesceNumber(
        overview?.raw_score_delta,
        selectedDelta?.raw_score_delta,
        selectedDelta?.delta,
        previousRawScore !== null && rawScore !== null ? rawScore - previousRawScore : null
      );

    const operationalScoreDelta = coalesceNumber(
        overview?.operational_score_delta,
        selectedDelta?.operational_score_delta,
        previousOperationalScore !== null && operationalScore !== null ? operationalScore - previousOperationalScore : null,
        rawScoreDelta
      );

    const openAlerts =
      safeNumber(alertsSummary?.by_status?.open) +
      safeNumber(alertsSummary?.by_status?.acknowledged) +
      safeNumber(alertsSummary?.by_status?.snoozed);

    const activeFailingControls = safeNumber(
      overview?.active_failing_controls,
      activeFindings + errorCount
    );

    const staleCount = safeNumber(overview?.stale_controls, overview?.stale_count || 0);

    const dataAsOf =
      overview?.data_as_of ||
      overview?.score_captured_at ||
      overview?.freshness_captured_at ||
      overview?.at ||
      null;

    const dataSources = ["posture", "freshness", "alerts"].join(" · ");

    return {
      latestScore: rawScore,
      rawScore,
      operationalScore,
      rawScoreDelta,
      operationalScoreDelta,
      previousRawScore,
      previousOperationalScore,
      delta: rawScoreDelta,
      openAlerts,
      failingControls: activeFailingControls,
      activeFindings,
      rawFailFindings: rawFailCount,
      rawNonpassingFindings,
      acceptedRiskFindings,
      errorCount,
      unknownCount,
      staleCount,
      closedTasks: closedRemediationCount,
      dataAsOf,
      dataSources,
      scoreAdjustmentReason:
        overview?.score_adjustment_reason ||
        "Operational Score separates evidence-backed approved exceptions from active unresolved work while preserving the raw score.",
    };
  }, [overview, postureDelta, alertsSummary, closedRemediationCount]);

  // Use the exact same refresh behavior as the existing Refresh view button.
  const refreshMissionControlView = useCallback(() => {
    if (refreshingView) return;

    manualRefreshRef.current = true;
    priorPostureTimestampRef.current = heroMetrics.dataAsOf || null;
    setRefreshMessage("");
    setRefreshError("");
    setRefreshNonce((current) => current + 1);
  }, [heroMetrics.dataAsOf, refreshingView]);

  // Shared deduplication for manual and scheduled completion notifications.
  const completedCaptureKeysRef = useRef(new Set());

  const handleCaptureCompleted = useCallback((completion = {}) => {
    const captureId = String(completion.capture_id || "");
    // Both manual and scheduled captures must be durably published and staged.
    // A terminal collection receipt alone is never a dashboard refresh event.
    if (!captureId ||
        completion.terminal_publication !== true ||
        completion.contribution_handoff_settled !== true ||
        completedCaptureKeysRef.current.has(captureId)) return;

    completedCaptureKeysRef.current.add(captureId);
    refreshMissionControlView();
  }, [refreshMissionControlView]);

  const askMissionControlIntelligence = useCallback(
    async (question, signal) => {
      const cleanedQuestion = String(question || "").trim();
      if (!cleanedQuestion || !tenantId || !userId) return null;

      setMissionControlIntelligence((current) => ({
        ...current,
        question: cleanedQuestion,
        loading: true,
        error: "",
      }));

      try {
        const token = await fetchToken();
        const framework =
          frameworkFilter === "all" ? undefined : frameworkFilter;

        const response = await postJsonWithToken(
          token,
          "/ai/copilot/ask",
          {
            question: cleanedQuestion,
            scope: "mission_control",
            copilot_context: {
              page: "mission_control",
              surface: "mission_control",
              ...(framework ? { framework } : {}),
            },
          },
          signal
        );

        const card =
          response?.card && typeof response.card === "object"
            ? response.card
            : null;

        if (!card) {
          throw new Error(
            "OASYS Intelligence returned no grounded Mission Control card."
          );
        }

        // Never publish a response from a superseded request.
        if (signal?.aborted) return null;

        setMissionControlIntelligence({
          question: cleanedQuestion,
          card,
          metadata:
            card?.metadata && typeof card.metadata === "object"
              ? card.metadata
              : response?.metadata || null,
          loading: false,
          error: "",
        });

        return card;
      } catch (e) {
        // Refresh cancellation is expected, not an intelligence failure.
        // Leave the loading state to the replacement request.
        if (
          signal?.aborted ||
          e?.name === "AbortError" ||
          e?.name === "CanceledError" ||
          String(e?.message || e || "") ===
            "mission-control-intelligence-refresh"
        ) {
          return null;
        }

        setMissionControlIntelligence((current) => ({
          ...current,
          loading: false,
          error: formatApiErrorMessage(e),
        }));

        return null;
      }
    },
    [
      fetchToken,
      frameworkFilter,
      postJsonWithToken,
      tenantId,
      userId,
    ]
  );

  useEffect(() => {
    if (loading || !tenantId || !userId) return undefined;

    const controller = new AbortController();

    askMissionControlIntelligence(
      "What needs attention now?",
      controller.signal
    );

    return () => controller.abort("mission-control-intelligence-refresh");
  }, [
    askMissionControlIntelligence,
    loading,
    refreshNonce,
    tenantId,
    userId,
  ]);

  const closeContextualExplain = useCallback(() => {
    setContextualExplain((current) => ({
      ...current,
      open: false,
      loading: false,
      error: "",
    }));
  }, []);

  useEffect(() => {
    const record =
      contextualExplain.open && contextualExplain.record
        ? contextualExplain.record
        : null;
    const recordType = String(contextualExplain.recordType || "").trim();
    const selectedFramework =
      frameworkFilter && frameworkFilter !== "all" ? frameworkFilter : undefined;

    const pageContext = {
      page: "mission_control",
      surface: "mission_control",
      intelligence_mode: record ? "contextual_explain" : "page",
      ...(selectedFramework ? { framework: selectedFramework } : {}),
      ...(frameworkFilter ? { filter: frameworkFilter } : {}),
    };

    if (!record) {
      clearCopilotContext();
      setCopilotContext(pageContext);
      return;
    }

    if (recordType === "validation") {
      setCopilotContext({
        ...pageContext,
        record_type: "validation",
        ...(record?.id ? { validation_result_id: record.id } : {}),
        ...(record?.validation_run_id
          ? { validation_run_id: record.validation_run_id }
          : {}),
        focus_label: String(record?.rule_id || record?.control_id || "Selected validation"),
        focus_type: "Validation result",
      });
      return;
    }

    if (recordType === "connector") {
      const connectorId = String(record?.connector_id || "").trim();
      if (connectorId) {
        setCopilotContext({
          ...pageContext,
          record_type: "connector",
          connector_id: connectorId,
          focus_label: String(record?.provider || "Selected connector"),
          focus_type: "Connector",
        });
        return;
      }
    }

    if (recordType === "priority_action") {
      const actionKey = String(record?.actionKey || "").trim();
      if (actionKey) {
        setCopilotContext({
          ...pageContext,
          record_type: "priority_action",
          priority_action_key: actionKey,
          ...(record?.actionType
            ? { priority_action_type: record.actionType }
            : {}),
          ...(record?.sourceType
            ? { priority_action_source_type: record.sourceType }
            : {}),
          focus_label: String(record?.title || "Selected priority action"),
          focus_type: "Priority action",
        });
        return;
      }
    }

    const findingId = String(
      record?.finding_id || record?.id || record?.record_id || ""
    ).trim();

    setCopilotContext({
      ...pageContext,
      record_type: "risk",
      ...(findingId ? { record_id: findingId } : {}),
      ...(record?.control_id ? { control_id: record.control_id } : {}),
      ...(record?.rule_id ? { rule_id: record.rule_id } : {}),
      ...(record?.resource_id || record?.resource
        ? { resource_id: record.resource_id || record.resource }
        : {}),
      focus_label: String(record?.rule_id || record?.control_id || "Selected risk"),
      focus_type: "Risk",
    });
  }, [
    clearCopilotContext,
    contextualExplain.open,
    contextualExplain.record,
    contextualExplain.recordType,
    frameworkFilter,
    setCopilotContext,
  ]);

  const explainMissionControlRisk = useCallback(
    async (risk, question = "Explain this risk.", signal) => {
      const cleanedQuestion = String(question || "").trim();
      if (!risk || !cleanedQuestion || !tenantId || !userId) return null;

      const selectedRecordId =
        risk?.finding_id || risk?.id || risk?.record_id || null;
      const sameRecord = Boolean(
        contextualExplain.record &&
          (
            (selectedRecordId &&
              (contextualExplain.record?.id === selectedRecordId ||
                contextualExplain.record?.record_id === selectedRecordId)) ||
            (!selectedRecordId &&
              String(contextualExplain.record?.framework || "") === String(risk?.framework || "") &&
              String(contextualExplain.record?.control_id || "") === String(risk?.control_id || "") &&
              String(contextualExplain.record?.rule_id || "") === String(risk?.rule_id || "") &&
              String(contextualExplain.record?.resource_id || contextualExplain.record?.resource || "") ===
                String(risk?.resource_id || risk?.resource || ""))
          )
      );

      setContextualExplain((current) => ({
        ...current,
        open: true,
        recordType: "risk",
        record: risk,
        question: cleanedQuestion,
        card: sameRecord ? current.card : null,
        metadata: sameRecord ? current.metadata : null,
        loading: true,
        error: "",
      }));

      try {
        const token = await fetchToken();
        const selectedFramework =
          risk?.framework ||
          (frameworkFilter === "all" ? undefined : frameworkFilter);
        const selectedResourceId = risk?.resource_id || risk?.resource || undefined;
        const selectedSource = risk?.source || risk?.provider || undefined;

        const response = await postJsonWithToken(
          token,
          "/ai/copilot/ask",
          {
            question: cleanedQuestion,
            scope: "mission_control",
            copilot_context: {
              page: "mission_control",
              surface: "mission_control",
              intelligence_mode: "contextual_explain",
              record_type: "risk",
              ...(selectedFramework ? { framework: selectedFramework } : {}),
              ...(selectedRecordId ? { record_id: selectedRecordId } : {}),
              ...(risk?.control_id ? { control_id: risk.control_id } : {}),
              ...(risk?.rule_id ? { rule_id: risk.rule_id } : {}),
              ...(selectedResourceId ? { resource_id: selectedResourceId } : {}),
              ...(selectedSource ? { source: selectedSource } : {}),
            },
          },
          signal
        );

        const card =
          response?.card && typeof response.card === "object"
            ? response.card
            : null;

        if (!card) {
          throw new Error(
            "OASYS Intelligence returned no grounded explanation for this selected risk."
          );
        }

        setContextualExplain({
          open: true,
          recordType: "risk",
          record: risk,
          question: cleanedQuestion,
          card,
          metadata:
            card?.metadata && typeof card.metadata === "object"
              ? card.metadata
              : response?.metadata || null,
          loading: false,
          error: "",
        });

        return card;
      } catch (e) {
        if (e?.name === "AbortError") return null;

        setContextualExplain((current) => ({
          ...current,
          open: true,
          record: risk,
          question: cleanedQuestion,
          loading: false,
          error: formatApiErrorMessage(e),
        }));

        return null;
      }
    },
    [
      contextualExplain.record,
      fetchToken,
      frameworkFilter,
      postJsonWithToken,
      tenantId,
      userId,
    ]
  );

  const explainMissionControlValidation = useCallback(
    async (
      validation,
      question = "Explain this validation.",
      signal
    ) => {
      const cleanedQuestion = String(question || "").trim();
      if (!validation || !cleanedQuestion || !tenantId || !userId) {
        return null;
      }

      const selectedResultId = validation?.id || null;
      const sameRecord = Boolean(
        contextualExplain.recordType === "validation" &&
          contextualExplain.record &&
          (
            (selectedResultId &&
              contextualExplain.record?.id === selectedResultId) ||
            (!selectedResultId &&
              String(contextualExplain.record?.validation_run_id || "") ===
                String(validation?.validation_run_id || "") &&
              String(contextualExplain.record?.rule_id || "") ===
                String(validation?.rule_id || "") &&
              String(contextualExplain.record?.resource_id || "") ===
                String(validation?.resource_id || ""))
          )
      );

      setContextualExplain((current) => ({
        ...current,
        open: true,
        recordType: "validation",
        record: validation,
        question: cleanedQuestion,
        card: sameRecord ? current.card : null,
        metadata: sameRecord ? current.metadata : null,
        loading: true,
        error: "",
      }));

      try {
        const token = await fetchToken();
        const selectedFramework =
          validation?.framework ||
          (frameworkFilter === "all" ? undefined : frameworkFilter);

        const response = await postJsonWithToken(
          token,
          "/ai/copilot/ask",
          {
            question: cleanedQuestion,
            scope: "mission_control",
            copilot_context: {
              page: "mission_control",
              surface: "mission_control",
              intelligence_mode: "contextual_explain",
              record_type: "validation",
              ...(selectedResultId
                ? { validation_result_id: selectedResultId }
                : {}),
              ...(validation?.validation_run_id
                ? { validation_run_id: validation.validation_run_id }
                : {}),
              ...(selectedFramework
                ? { framework: selectedFramework }
                : {}),
              ...(validation?.control_id
                ? { control_id: validation.control_id }
                : {}),
              ...(validation?.rule_id
                ? { rule_id: validation.rule_id }
                : {}),
              ...(validation?.resource_id
                ? { resource_id: validation.resource_id }
                : {}),
              ...(validation?.provider
                ? { provider: validation.provider }
                : {}),
              ...(validation?.stream
                ? { stream: validation.stream }
                : {}),
              ...(
                validation?.result || validation?.status
                  ? {
                      validation_status:
                        validation?.result || validation?.status,
                    }
                  : {}
              ),
            },
          },
          signal
        );

        const card =
          response?.card && typeof response.card === "object"
            ? response.card
            : null;

        if (!card) {
          throw new Error(
            "OASYS Intelligence returned no grounded explanation for this selected validation."
          );
        }

        setContextualExplain({
          open: true,
          recordType: "validation",
          record: validation,
          question: cleanedQuestion,
          card,
          metadata:
            card?.metadata && typeof card.metadata === "object"
              ? card.metadata
              : response?.metadata || null,
          loading: false,
          error: "",
        });

        return card;
      } catch (e) {
        if (e?.name === "AbortError") return null;

        setContextualExplain((current) => ({
          ...current,
          open: true,
          recordType: "validation",
          record: validation,
          question: cleanedQuestion,
          loading: false,
          error: formatApiErrorMessage(e),
        }));

        return null;
      }
    },
    [
      contextualExplain.record,
      contextualExplain.recordType,
      fetchToken,
      frameworkFilter,
      postJsonWithToken,
      tenantId,
      userId,
    ]
  );

  const connectorHealthMetrics = useMemo(() => {
    const summary = connectorHealth?.summary || {};
    const byHealth = summary.by_operational_health || {};
    const connectors = Array.isArray(connectorHealth?.connectors)
      ? connectorHealth.connectors
      : [];

    const unhealthyCount =
      safeNumber(byHealth.degraded) +
      safeNumber(byHealth.failing) +
      safeNumber(byHealth.stale) +
      safeNumber(byHealth.unknown);

    const issueCandidates = connectors
      .map((connector) => {
        const provider = connector.provider;
        const activeDlq = safeNumber(connector.dlq_count);
        const health = String(connector.operational_health || "unknown").toLowerCase();
        const connectionStatus = String(connector.connection_status || "unknown").toLowerCase();

        const severity =
          health === "failing" || connectionStatus === "disconnected"
            ? 4
            : health === "stale"
            ? 3
            : health === "degraded"
            ? 2
            : activeDlq > 0
            ? 1
            : 0;

        return {
          ...connector,
          provider,
          connector_id: connector.connector_id || null,
          activeDlq,
          health,
          connectionStatus,
          recommendedAction: connector.recommended_action || "",
          severity,
        };
      })
      .filter((item) => item.severity > 0)
      .sort((a, b) => b.severity - a.severity || b.activeDlq - a.activeDlq);

    return {
      loaded: Boolean(connectorHealth),
      total: safeNumber(summary.total_connectors, connectors.length),
      healthy: safeNumber(byHealth.healthy),
      degraded: safeNumber(byHealth.degraded),
      failing: safeNumber(byHealth.failing),
      stale: safeNumber(byHealth.stale),
      unknown: safeNumber(byHealth.unknown),
      unhealthyCount,
      connectors,
      topIssue: issueCandidates[0] || null,
    };
  }, [connectorHealth]);

  // R6Y: one authenticated event stream for manual and scheduled completions.
  // A reconnect rechecks authoritative durable state on the server.
  const captureEligibleAwsConnectors = connectorHealthMetrics.connectors.filter(
    (item) => String(item.provider || "").toLowerCase() === "aws" &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(item.connector_id || ""))
  );
  const captureAutoConnectorId = captureEligibleAwsConnectors.length === 1 ? captureEligibleAwsConnectors[0].connector_id : null;
  const captureAutoFeatureOn = process.env.REACT_APP_OPERATIONAL_CAPTURE_UI_ENABLED === "1";
  const deliveredCaptureScopeRef = useRef(null);
  useEffect(() => {
    if (!captureAutoFeatureOn || !captureAutoConnectorId || loading || err) return undefined;
    const scope = `${tenantId || ""}:${captureAutoConnectorId}`;
    if (deliveredCaptureScopeRef.current !== scope) {
      deliveredCaptureScopeRef.current = scope;
      completedCaptureKeysRef.current.clear();
    }
    const controller = new AbortController();
    let stopped = false;
    const stream = async () => {
      let delayMs = 1000;
      while (!stopped) {
        try {
          const token = await fetchToken();
          const response = await fetch(`${String(apiBaseUrl || "").replace(/\/$/, "")}/operational-capture/completion-events`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json",
              ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
              ...(userId ? { "X-User-Id": userId } : {}) },
            body: JSON.stringify({ connector_id: captureAutoConnectorId }),
            signal: controller.signal,
          });
          if (!response.ok || !response.body) {
            // Disable silent retry storms when rollout/auth is unavailable.
            if ([401, 403, 404].includes(response.status)) return;
            throw new Error(`completion stream HTTP ${response.status}`);
          }
          delayMs = 1000;
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          while (!stopped) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let split;
            while ((split = buffer.indexOf("\n\n")) !== -1) {
              const frame = buffer.slice(0, split).replace(/\r/g, "");
              buffer = buffer.slice(split + 2);
              if (!frame.startsWith("event: capture-complete\n")) continue;
              const data = frame.split("\n").find((line) => line.startsWith("data: "));
              if (!data) continue;
              let event;
              try { event = JSON.parse(data.slice(6)); } catch (_) { continue; }
              if (!event?.capture_id || event?.terminal_publication !== true ||
                  event?.contribution_handoff_settled !== true) continue;
              window.dispatchEvent(new CustomEvent("oasys:capture-complete", { detail: event }));
              handleCaptureCompleted(event);
            }
          }
        } catch (error) {
          if (stopped || controller.signal.aborted) return;
        }
        if (stopped) break;
        await new Promise((resolve) => {
          // Only reconnect a broken stream; never poll backend status.
          const timer = setTimeout(resolve, delayMs);
          controller.signal.addEventListener("abort", () => { clearTimeout(timer); resolve(); }, { once: true });
        });
        delayMs = Math.min(delayMs * 2, 30000);
      }
    };
    stream();
    return () => { stopped = true; controller.abort(); };
  }, [captureAutoFeatureOn, captureAutoConnectorId, tenantId, userId, apiBaseUrl, loading, err, fetchToken, handleCaptureCompleted]);

  const explainMissionControlConnector = useCallback(
    async (
      connector,
      question = "Explain this connector.",
      signal
    ) => {
      const cleanedQuestion = String(question || "").trim();
      if (!connector || !cleanedQuestion || !tenantId || !userId) {
        return null;
      }

      const selectedConnectorId = connector?.connector_id || null;
      if (!selectedConnectorId) {
        return null;
      }

      const sameRecord = Boolean(
        contextualExplain.recordType === "connector" &&
          contextualExplain.record &&
          String(contextualExplain.record?.connector_id || "") ===
            String(selectedConnectorId)
      );

      setContextualExplain((current) => ({
        ...current,
        open: true,
        recordType: "connector",
        record: connector,
        question: cleanedQuestion,
        card: sameRecord ? current.card : null,
        metadata: sameRecord ? current.metadata : null,
        loading: true,
        error: "",
      }));

      try {
        const token = await fetchToken();

        const response = await postJsonWithToken(
          token,
          "/ai/copilot/ask",
          {
            question: cleanedQuestion,
            scope: "mission_control",
            copilot_context: {
              page: "mission_control",
              surface: "mission_control",
              intelligence_mode: "contextual_explain",
              record_type: "connector",
              connector_id: selectedConnectorId,
              ...(connector?.provider
                ? { provider: connector.provider }
                : {}),
            },
          },
          signal
        );

        const card =
          response?.card && typeof response.card === "object"
            ? response.card
            : null;

        if (!card) {
          throw new Error(
            "OASYS Intelligence returned no grounded explanation for this selected connector."
          );
        }

        setContextualExplain({
          open: true,
          recordType: "connector",
          record: connector,
          question: cleanedQuestion,
          card,
          metadata:
            card?.metadata && typeof card.metadata === "object"
              ? card.metadata
              : response?.metadata || null,
          loading: false,
          error: "",
        });

        return card;
      } catch (e) {
        if (e?.name === "AbortError") return null;

        setContextualExplain((current) => ({
          ...current,
          open: true,
          recordType: "connector",
          record: connector,
          question: cleanedQuestion,
          loading: false,
          error: formatApiErrorMessage(e),
        }));

        return null;
      }
    },
    [
      contextualExplain.record,
      contextualExplain.recordType,
      fetchToken,
      postJsonWithToken,
      tenantId,
      userId,
    ]
  );

  const identityRiskMetrics = useMemo(() => {
    const findings = (Array.isArray(identityFindings) ? identityFindings : [])
      .filter((finding) => isIdentityInventoryFinding(finding))
      .map((finding) => {
        const ruleId = String(finding.rule_id || "").trim();
        const provider = getFindingProvider(finding);
        const state = normalizeFindingState(finding.state || finding.status);
        const resourceId = String(finding.resource_id || finding.resource || "").trim();
        const resourceType = String(finding.resource_type || "").trim();

        return {
          ...finding,
          ruleId,
          provider,
          state,
          resourceId,
          resourceType,
          label: formatIdentityRule(ruleId),
          isFailing: isFailingFinding(finding),
        };
      });

    const providerCounts = findings.reduce(
      (acc, finding) => {
        if (finding.provider === "google_workspace") acc.googleWorkspace += 1;
        if (finding.provider === "okta") acc.okta += 1;
        return acc;
      },
      { googleWorkspace: 0, okta: 0 }
    );

    const failed = findings.filter((finding) => finding.isFailing);
    const mfaFindings = findings.filter((finding) => IDENTITY_CATEGORY_RULES.mfa.has(finding.ruleId));
    const adminFindings = findings.filter((finding) => IDENTITY_CATEGORY_RULES.admin.has(finding.ruleId));
    const lifecycleFindings = findings.filter((finding) =>
      IDENTITY_CATEGORY_RULES.lifecycle.has(finding.ruleId)
    );

    const topFinding =
      failed.find((finding) => IDENTITY_CATEGORY_RULES.mfa.has(finding.ruleId)) ||
      failed[0] ||
      findings.find((finding) => finding.ruleId === "OKTA_STALE_ADMIN") ||
      findings[0] ||
      null;

    return {
      loaded: Array.isArray(identityFindings),
      total: findings.length,
      failedCount: failed.length,
      passedCount: findings.filter((finding) => finding.state === "pass").length,
      googleWorkspaceCount: providerCounts.googleWorkspace,
      oktaCount: providerCounts.okta,
      mfaRiskCount: mfaFindings.filter((finding) => finding.isFailing).length,
      adminRiskCount: adminFindings.filter((finding) => finding.isFailing).length,
      lifecycleRiskCount: lifecycleFindings.filter((finding) => finding.isFailing).length,
      failed,
      topFinding,
    };
  }, [identityFindings]);

  const connectorEvidenceMetrics = useMemo(() => {
    const findings = (Array.isArray(identityFindings) ? identityFindings : [])
      .filter((finding) => isConnectorEvidenceFinding(finding))
      .map((finding) => {
        const ruleId = String(finding.rule_id || "").trim();
        const provider = getFindingProvider(finding);
        const state = normalizeFindingState(finding.state || finding.status);
        const resourceId = String(finding.resource_id || finding.resource || "").trim();
        const resourceType = String(finding.resource_type || "").trim();

        return {
          ...finding,
          ruleId,
          provider,
          state,
          resourceId,
          resourceType,
          label: formatConnectorEvidenceRule(ruleId),
          isFailing: isFailingFinding(finding),
          isUnknown: state === "unknown",
        };
      });

    const providerCounts = findings.reduce(
      (acc, finding) => {
        if (finding.provider === "github") acc.github += 1;
        if (finding.provider === "cloudflare") acc.cloudflare += 1;
        return acc;
      },
      { github: 0, cloudflare: 0 }
    );

    const failed = findings.filter((finding) => finding.isFailing);
    const unknown = findings.filter((finding) => finding.isUnknown);
    const codeFindings = findings.filter((finding) =>
      CONNECTOR_EVIDENCE_CATEGORY_RULES.code.has(finding.ruleId)
    );
    const edgeFindings = findings.filter((finding) =>
      CONNECTOR_EVIDENCE_CATEGORY_RULES.edge.has(finding.ruleId)
    );
    const accessLimitedFindings = findings.filter((finding) =>
      CONNECTOR_EVIDENCE_CATEGORY_RULES.accessLimited.has(finding.ruleId)
    );

    const topFinding =
      failed.find((finding) => finding.ruleId === "CF_TLS_MIN_VERSION_WEAK") ||
      failed.find((finding) => finding.ruleId === "CF_WAF_DISABLED") ||
      failed.find((finding) => finding.ruleId === "GH_DEPENDABOT_DISABLED") ||
      failed[0] ||
      unknown[0] ||
      findings[0] ||
      null;

    return {
      loaded: Array.isArray(identityFindings),
      total: findings.length,
      failedCount: failed.length,
      passedCount: findings.filter((finding) => finding.state === "pass").length,
      unknownCount: unknown.length,
      githubCount: providerCounts.github,
      cloudflareCount: providerCounts.cloudflare,
      codeRiskCount: codeFindings.filter((finding) => finding.isFailing).length,
      edgeRiskCount: edgeFindings.filter((finding) => finding.isFailing).length,
      accessLimitedCount: accessLimitedFindings.filter((finding) => finding.isUnknown).length,
      failed,
      unknown,
      topFinding,
    };
  }, [identityFindings]);



  const workflowHealthMetrics = useMemo(() => {
    const summary = workflowSummary || {};
    const bottlenecks = Array.isArray(summary.bottlenecks_by_step_type)
      ? summary.bottlenecks_by_step_type
      : [];

    const blocked = safeNumber(summary.blocked);
    const failed = safeNumber(summary.failed);
    const awaitingApproval = safeNumber(summary.awaiting_approval);
    const awaitingValidation = safeNumber(summary.awaiting_validation);
    const dependencyPending = safeNumber(summary.dependency_pending_count);
    const dependencyBlocked = safeNumber(summary.dependency_blocked_count);
    const dependencyFailed = safeNumber(summary.dependency_failed_count);
    const dependencyIssues = dependencyPending + dependencyBlocked + dependencyFailed;
    const running = safeNumber(summary.running);
    const queued = safeNumber(summary.queued);
    const completedToday = safeNumber(summary.completed_today);
    const createdToday = safeNumber(summary.created_today);
    const topBottleneck = bottlenecks[0] || null;

    const healthTone =
      blocked > 0 || failed > 0 || dependencyBlocked > 0 || dependencyFailed > 0
        ? "high"
        : awaitingApproval > 0 || awaitingValidation > 0 || dependencyPending > 0
        ? "medium"
        : "low";

    const healthLabel =
      blocked > 0
        ? `${blocked} blocked`
        : failed > 0
        ? `${failed} failed`
        : awaitingApproval > 0
        ? `${awaitingApproval} approval`
        : awaitingValidation > 0
        ? `${awaitingValidation} validation`
        : running > 0
        ? `${running} running`
        : "Clear";

    return {
      loaded: Boolean(workflowSummary),
      total: safeNumber(summary.total),
      running,
      queued,
      waiting: safeNumber(summary.waiting),
      assigned: safeNumber(summary.assigned),
      executing: safeNumber(summary.executing),
      blocked,
      failed,
      awaitingApproval,
      awaitingValidation,
      completed: safeNumber(summary.completed),
      cancelled: safeNumber(summary.cancelled),
      createdToday,
      completedToday,
      averageDuration: summary.average_duration_seconds,
      approvalWaitingCount: safeNumber(summary.approval_waiting_count),
      validationWaitingCount: safeNumber(summary.validation_waiting_count),
      dependencyPending,
      dependencyBlocked,
      dependencyFailed,
      dependencyIssues,
      bottlenecks,
      topBottleneck,
      healthTone,
      healthLabel,
    };
  }, [workflowSummary]);

  const validationMetrics = useMemo(() => {
    const payload = validationDashboard || {};
    const metrics = payload.metrics || payload || {};
    const summary = payload.summary || {};
    const sections = payload.sections || {};
    const runTotals = metrics.run_totals || {};
    const resultTotals = metrics.result_totals || {};
    const confidence =
      summary.compliance_confidence ||
      metrics.compliance_confidence ||
      {};
    const evidenceCoverage = metrics.evidence_coverage || sections.evidence_coverage || {};
    const controlHealth = metrics.control_health || sections.control_health || {};
    const controlSummary = controlHealth.summary || {};
    const failedValidations = Array.isArray(sections.failed_validations)
      ? sections.failed_validations
      : [];
    const latestFailedValidation = failedValidations[0] || null;

    const failedResults = safeNumber(
      resultTotals.failed_results,
      safeNumber(summary.failed_results, failedValidations.length)
    );
    const pendingRuns = safeNumber(runTotals.pending_runs, safeNumber(summary.pending_runs));
    const health = metrics.validation_health || summary.validation_health || "unknown";
    const tone = validationHealthTone(health, failedResults, pendingRuns);
    const confidenceScore = coalesceNumber(confidence.score, confidence.confidence_score);
    const confidenceLevel = String(confidence.level || confidence.confidence_level || "unknown");

    return {
      loaded: Boolean(validationDashboard),
      health,
      healthLabel: formatValidationHealth(health),
      tone,
      totalRuns: safeNumber(runTotals.total_runs, safeNumber(summary.total_runs)),
      pendingRuns,
      failedRuns: safeNumber(runTotals.failed_runs),
      totalResults: safeNumber(resultTotals.total_results, safeNumber(summary.total_results)),
      failedResults,
      passedResults: safeNumber(resultTotals.passed_results),
      successRate: safeNumber(metrics.success_rate, safeNumber(summary.success_rate)),
      failureRate: safeNumber(metrics.failure_rate, safeNumber(summary.failure_rate)),
      confidenceScore,
      confidenceLevel,
      confidenceHealth: confidence.health || "unknown",
      confidenceReason: confidence.reason || "No confidence reason available.",
      evidenceCompletenessPct: safeNumber(evidenceCoverage.completeness_pct),
      evidenceFreshnessPct: safeNumber(evidenceCoverage.freshness_pct),
      evidenceLinks: safeNumber(evidenceCoverage.total_links),
      failingControls: safeNumber(controlSummary.failing_controls),
      repeatFailureControls: safeNumber(controlSummary.repeat_failure_controls),
      failedValidations,
      latestFailedValidation,
      generatedAt: payload.generated_at || metrics.generated_at || null,
    };
  }, [validationDashboard]);

  const recommendationContext = useMemo(() => {
    const failedValidationRows = validationMetrics.failedValidations.map((item) => ({
      connector: item.connector || item.provider || item.affected_connector || item.source,
      framework: item.framework || item.affected_framework || overview?.framework,
      control_id: item.control_id || item.control || item.rule_control_id,
      finding_id: item.finding_id || item.rule_id || item.rule,
      resource_id: item.resource_id || item.resource,
      closure_blocked: item.closure_blocked ?? validationMetrics.failedResults > 0,
      validation_failed: true,
      external_exposure: item.external_exposure,
      validation_confidence_score:
        item.validation_confidence_score || validationMetrics.confidenceScore || 75,
      task_id: item.task_id || item.remediation_task_id,
    }));

    const connectorRows = connectorHealthMetrics.connectors.map((connector) => ({
      provider: connector.provider,
      connector: connector.provider,
      status: connector.operational_health || connector.connection_status,
      operational_health: connector.operational_health,
      connection_status: connector.connection_status,
      dlq_count: connector.dlq_count,
      recommended_action: connector.recommended_action,
      freshness_debt:
        String(connector.operational_health || "").toLowerCase() === "stale" ||
        String(connector.operational_health || "").toLowerCase() === "degraded",
      connector_degraded: ["degraded", "stale", "unknown"].includes(
        String(connector.operational_health || "").toLowerCase()
      ),
      connector_unhealthy: ["failing", "unhealthy", "disconnected"].includes(
        String(connector.operational_health || connector.connection_status || "").toLowerCase()
      ),
    }));

    const staleControlRows = staleControls.map((control) => ({
      framework: control.framework || overview?.framework,
      control_id: control.control_id,
      finding_id: control.rule_id,
      resource_id: control.resource_id || control.resource,
      connector: control.source || control.provider,
      age_hours: control.age_hours || control.stale_hours,
    }));

    const topRiskRows = topRisks.map((risk) => ({
      title: risk.title || risk.rule_id || risk.control_id || "Top risk",
      framework: risk.framework || overview?.framework,
      control_id: risk.control_id,
      finding_id: risk.rule_id || risk.finding_id,
      resource_id: risk.resource_id || risk.resource,
      connector: risk.source || risk.provider,
      reason_summary: risk.reason_summary,
      external_exposure: risk.external_exposure,
      validation_failed: risk.validation_failed,
      closure_blocked: risk.closure_blocked,
    }));

    const sopMissingRows = sopPendingApproval.count > 0
      ? [
          {
            framework: overview?.framework || "SOC2",
            control_id: overview?.control_id || "document-control",
            summary: `${sopPendingApproval.count} SOP revision${
              sopPendingApproval.count === 1 ? "" : "s"
            } awaiting approval.`,
          },
        ]
      : [];

    return {
      mission_control: {
        top_risks: topRiskRows,
        stale_controls: staleControlRows,
        validation_blockers: failedValidationRows,
        connector_health: connectorRows,
        blocked_workflows: workflowHealthMetrics.blocked > 0 ? [{ count: workflowHealthMetrics.blocked }] : [],
      },
      executive: {
        overall_posture_score: heroMetrics.operationalScore,
        posture_delta: heroMetrics.operationalScoreDelta,
        top_risks: topRiskRows,
        failed_validation_count: validationMetrics.failedResults,
        closure_blocked_count: failedValidationRows.filter((item) => item.closure_blocked).length,
        stale_evidence_count: heroMetrics.staleCount,
        degraded_connector_count: connectorHealthMetrics.unhealthyCount,
      },
      workflow: {
        blocked_workflows: workflowHealthMetrics.blocked > 0 ? [{ count: workflowHealthMetrics.blocked }] : [],
        approval_bottlenecks:
          workflowHealthMetrics.awaitingApproval > 0
            ? [{ count: workflowHealthMetrics.awaitingApproval }]
            : [],
      },
      connectors: {
        connector_health: connectorRows,
        stale_connectors: connectorRows.filter((item) => item.freshness_debt),
      },
      validation: {
        failed_validations: failedValidationRows,
      },
      sop: {
        missing_sops: sopMissingRows,
        outdated_sops: sopOverdue.count > 0 ? [{ framework: overview?.framework || "SOC2", count: sopOverdue.count }] : [],
      },
      evidence: {
        missing_evidence: failedValidationRows.map((item) => ({
          ...item,
          evidence_missing: true,
        })),
        stale_evidence: staleControlRows,
      },
      freshness: {
        stale_controls: staleControlRows,
        stale_connectors: connectorRows.filter((item) => item.freshness_debt),
        framework_freshness_debt:
          heroMetrics.staleCount > 0
            ? [
                {
                  framework: overview?.framework || "SOC2",
                  stale_count: heroMetrics.staleCount,
                  freshness_score: Math.max(0, 100 - heroMetrics.staleCount * 5),
                },
              ]
            : [],
      },
      trend: {
        posture_delta: heroMetrics.operationalScoreDelta,
        overall_posture_score: heroMetrics.operationalScore,
        repeated_findings: topRiskRows
          .filter((risk) => risk.finding_id)
          .slice(0, 3)
          .map((risk) => ({ ...risk, count: 2 })),
        repeated_validation_failures: failedValidationRows.map((item) => ({ ...item, count: 2 })),
        recurring_connector_issues: connectorRows
          .filter((item) => item.connector_degraded || item.connector_unhealthy)
          .map((item) => ({ ...item, provider: item.provider, count: 2 })),
      },
    };
  }, [
    connectorHealthMetrics,
    heroMetrics,
    overview?.control_id,
    overview?.framework,
    sopOverdue.count,
    sopPendingApproval.count,
    staleControls,
    topRisks,
    validationMetrics,
    workflowHealthMetrics,
  ]);

  useEffect(() => {
    if (loading || err || !tenantId || !userId) return undefined;

    let mounted = true;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort("Recommendation request timed out");
    }, REQUEST_TIMEOUT_MS);

    setRecommendationLoading(true);
    setRecommendationError(null);

    (async () => {
      try {
        const token = await fetchToken();
        const payload = {
          surface: "all",
          limit: 3,
          mark_shown: true,
          context: recommendationContext,
        };

        const data = await postJsonWithToken(
          token,
          "/recommendations/generate",
          payload,
          controller.signal
        );

        if (!mounted) return;
        setRecommendationSummary(data || null);
      } catch (e) {
        if (!mounted || e?.name === "AbortError") return;
        console.warn("Recommendation summary unavailable:", e);
        setRecommendationError(String(e?.message || e));
      } finally {
        clearTimeout(timeoutId);
        if (mounted) setRecommendationLoading(false);
      }
    })();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      controller.abort("recommendations-unmounted");
    };
  }, [
    err,
    fetchToken,
    loading,
    postJsonWithToken,
    recommendationContext,
    tenantId,
    userId,
  ]);

  useEffect(() => {
    if (!recommendationFeedbackState.message) return undefined;

    const timeoutId = window.setTimeout(() => {
      setRecommendationFeedbackState((current) =>
        current.message
          ? {
              ...current,
              message: "",
            }
          : current
      );
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [recommendationFeedbackState.message]);

  useEffect(() => {
    if (!dismissedRecommendationsMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setDismissedRecommendationsMessage("");
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [dismissedRecommendationsMessage]);

  useEffect(() => {
    if (!acceptedRecommendationsMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setAcceptedRecommendationsMessage("");
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [acceptedRecommendationsMessage]);

  useEffect(() => {
    if (!draftHandoffsMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setDraftHandoffsMessage("");
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [draftHandoffsMessage]);

  const loadDismissedRecommendations = useCallback(
    async () => {
      setDismissedRecommendationsLoading(true);
      setDismissedRecommendationsError("");

      try {
        const token = await fetchToken();
        const data = await getJsonWithToken(
          token,
          "/recommendations/dismissed",
          { limit: 25 }
        );

        setDismissedRecommendations({
          items: Array.isArray(data?.items) ? data.items : [],
          count: safeNumber(data?.count, 0),
          loaded: true,
        });
      } catch (e) {
        console.warn("Dismissed recommendations unavailable:", e);
        setDismissedRecommendationsError(String(e?.message || e));
      } finally {
        setDismissedRecommendationsLoading(false);
      }
    },
    [fetchToken, getJsonWithToken]
  );

  const openDismissedRecommendationsModal = useCallback(() => {
    setShowDismissedRecommendationsModal(true);
    setDismissedRecommendationsMessage("");
    loadDismissedRecommendations();
  }, [loadDismissedRecommendations]);

  const restoreDismissedRecommendation = useCallback(
    async (recommendation = {}) => {
      const recommendationId = recommendation?.id;
      if (!recommendationId) {
        setDismissedRecommendationsError("Restore requires a persisted recommendation ID.");
        return;
      }

      setRestoringRecommendationId(recommendationId);
      setDismissedRecommendationsError("");
      setDismissedRecommendationsMessage("");

      try {
        const token = await fetchToken();
        const data = await postJsonWithToken(
          token,
          `/recommendations/${recommendationId}/restore`,
          {}
        );

        const restoredTitle = recommendation.title || "Recommendation";
        setDismissedRecommendations((current) => {
          const currentItems = Array.isArray(current.items) ? current.items : [];
          const nextItems = currentItems.filter(
            (item) => String(item?.id || "") !== String(recommendationId)
          );

          return {
            ...current,
            items: nextItems,
            count: Math.max(0, safeNumber(current.count, currentItems.length) - 1),
            loaded: true,
          };
        });

        setDismissedRecommendationsMessage(`${restoredTitle} restored to active recommendations.`);

        if (data?.result?.new_status === "active") {
          setRecommendationSummary((current) =>
            current
              ? {
                  ...current,
                  restored_recommendation_id: recommendationId,
                }
              : current
          );
        }
      } catch (e) {
        console.warn("Recommendation restore failed:", e);
        setDismissedRecommendationsError(String(e?.message || e));
      } finally {
        setRestoringRecommendationId(null);
      }
    },
    [fetchToken, postJsonWithToken]
  );

  const loadAcceptedRecommendations = useCallback(
    async () => {
      setAcceptedRecommendationsLoading(true);
      setAcceptedRecommendationsError("");

      try {
        const token = await fetchToken();
        const data = await getJsonWithToken(
          token,
          "/recommendations/accepted",
          { limit: 25 }
        );

        setAcceptedRecommendations({
          items: Array.isArray(data?.items) ? data.items : [],
          count: safeNumber(data?.count, 0),
          loaded: true,
        });
      } catch (e) {
        console.warn("Accepted recommendations unavailable:", e);
        setAcceptedRecommendationsError(String(e?.message || e));
      } finally {
        setAcceptedRecommendationsLoading(false);
      }
    },
    [fetchToken, getJsonWithToken]
  );

  const openAcceptedRecommendationsModal = useCallback(() => {
    setShowAcceptedRecommendationsModal(true);
    setAcceptedRecommendationsMessage("");
    loadAcceptedRecommendations();
  }, [loadAcceptedRecommendations]);

  const loadDraftRecommendationHandoffs = useCallback(
    async () => {
      setDraftHandoffsLoading(true);
      setDraftHandoffsError("");

      try {
        const token = await fetchToken();
        const data = await getJsonWithToken(
          token,
          "/recommendations/handoffs/draft",
          { limit: 25 }
        );

        setDraftHandoffs({
          items: Array.isArray(data?.items) ? data.items : [],
          count: safeNumber(data?.count, 0),
          loaded: true,
        });
      } catch (e) {
        console.warn("Draft handoffs unavailable:", e);
        setDraftHandoffsError(String(e?.message || e));
      } finally {
        setDraftHandoffsLoading(false);
      }
    },
    [fetchToken, getJsonWithToken]
  );

  const loadConvertedRecommendationHandoffs = useCallback(async () => {
    setConvertedHandoffsLoading(true);
    setConvertedHandoffsError("");

    try {
      const token = await fetchToken();
      const data = await getJsonWithToken(
        token,
        "/recommendations/handoffs/converted",
        { limit: 25 }
      );

      setConvertedHandoffs({
        items: Array.isArray(data?.items) ? data.items : [],
        count: Number.isFinite(Number(data?.count)) ? Number(data.count) : 0,
        loaded: true,
      });
    } catch (e) {
      console.warn("Converted handoffs recall failed:", e);
      setConvertedHandoffsError(String(e?.message || e));
    } finally {
      setConvertedHandoffsLoading(false);
    }
  }, [fetchToken, getJsonWithToken]);

  const openConvertedHandoffsModal = useCallback(() => {
    setShowConvertedHandoffsModal(true);
    setConvertedHandoffsMessage("");
    loadConvertedRecommendationHandoffs();
  }, [loadConvertedRecommendationHandoffs]);

  const openDraftHandoffsModal = useCallback(() => {
    setShowDraftHandoffsModal(true);
    setDraftHandoffsMessage("");
    loadDraftRecommendationHandoffs();
  }, [loadDraftRecommendationHandoffs]);

  const openDraftHandoffsFromHandoffPreview = useCallback(() => {
    setShowRecommendationHandoffPreviewModal(false);
    setRecommendationHandoffPreviewError("");
    setDraftHandoffError("");
    setShowDraftHandoffsModal(true);
    setDraftHandoffsMessage("");
    loadDraftRecommendationHandoffs();
  }, [loadDraftRecommendationHandoffs]);

  const previewDraftHandoffConversion = useCallback(
    async (handoff = {}) => {
      const handoffId = handoff?.id;
      if (!handoffId) {
        setDraftHandoffsError("Conversion preview requires a draft handoff ID.");
        return;
      }

      setPreviewingConvertHandoffId(handoffId);
      setHandoffConvertPreview(null);
      setHandoffConvertPreviewError("");
      setHandoffConversionResult(null);
      setHandoffConversionError("");
      setHandoffConvertPreviewLoading(true);
      setShowHandoffConvertPreviewModal(true);

      try {
        const token = await fetchToken();
        const data = await postJsonWithToken(
          token,
          `/recommendations/handoffs/${handoffId}/convert/preview`,
          {}
        );

        setHandoffConvertPreview(data || null);
      } catch (e) {
        console.warn("Draft handoff convert preview failed:", e);
        setHandoffConvertPreviewError(String(e?.message || e));
      } finally {
        setHandoffConvertPreviewLoading(false);
        setPreviewingConvertHandoffId(null);
      }
    },
    [fetchToken, postJsonWithToken]
  );

  const confirmDraftHandoffConversion = useCallback(
    async () => {
      const payload = handoffConvertPreview?.preview || handoffConvertPreview || {};
      const handoffId =
        payload?.handoff_id ||
        handoffConvertPreview?.handoff_id ||
        handoffConvertPreview?.preview?.handoff_id;

      if (!handoffId) {
        setHandoffConversionError("Conversion requires a draft handoff ID.");
        return;
      }

      setConfirmingHandoffConversion(true);
      setHandoffConversionError("");

      try {
        const token = await fetchToken();
        const data = await postJsonWithToken(
          token,
          `/recommendations/handoffs/${handoffId}/convert`,
          {}
        );

        setHandoffConversionResult(data || null);
        setDraftHandoffsMessage("Handoff converted to remediation task. Draft queue refreshed.");

        await loadDraftRecommendationHandoffs();
        await loadConvertedRecommendationHandoffs();
      } catch (e) {
        console.warn("Draft handoff conversion failed:", e);
        setHandoffConversionError(formatApiErrorMessage(e));
      } finally {
        setConfirmingHandoffConversion(false);
      }
    },
    [fetchToken, handoffConvertPreview, loadDraftRecommendationHandoffs, loadConvertedRecommendationHandoffs, postJsonWithToken]
  );

  const cancelDraftRecommendationHandoff = useCallback(
    async (handoff = {}) => {
      const handoffId = handoff?.id;
      if (!handoffId) {
        setDraftHandoffsError("Cancel requires a draft handoff ID.");
        return;
      }

      setCancellingDraftHandoffId(handoffId);
      setDraftHandoffsError("");
      setDraftHandoffsMessage("");

      try {
        const token = await fetchToken();
        await postJsonWithToken(
          token,
          `/recommendations/handoffs/${handoffId}/cancel`,
          {}
        );

        const cancelledTitle = handoff.title || handoff.suggested_task_title || "Draft handoff";
        setDraftHandoffs((current) => {
          const currentItems = Array.isArray(current.items) ? current.items : [];
          const nextItems = currentItems.filter(
            (item) => String(item?.id || "") !== String(handoffId)
          );

          return {
            ...current,
            items: nextItems,
            count: Math.max(0, safeNumber(current.count, currentItems.length) - 1),
            loaded: true,
          };
        });

        setDraftHandoffResult((current) => {
          const currentHandoffId = current?.handoff?.id;
          if (String(currentHandoffId || "") !== String(handoffId)) return current;
          return null;
        });

        setDraftHandoffsMessage(`${cancelledTitle} cancelled. No remediation or workflow execution occurred.`);
      } catch (e) {
        console.warn("Draft handoff cancel failed:", e);
        setDraftHandoffsError(String(e?.message || e));
      } finally {
        setCancellingDraftHandoffId(null);
      }
    },
    [fetchToken, postJsonWithToken]
  );

  const restoreAcceptedRecommendation = useCallback(
    async (recommendation = {}) => {
      const recommendationId = recommendation?.id;
      if (!recommendationId) {
        setAcceptedRecommendationsError("Restore requires a persisted recommendation ID.");
        return;
      }

      setRestoringRecommendationId(recommendationId);
      setAcceptedRecommendationsError("");
      setAcceptedRecommendationsMessage("");

      try {
        const token = await fetchToken();
        const data = await postJsonWithToken(
          token,
          `/recommendations/${recommendationId}/restore`,
          {}
        );

        const restoredTitle = recommendation.title || "Recommendation";
        setAcceptedRecommendations((current) => {
          const currentItems = Array.isArray(current.items) ? current.items : [];
          const nextItems = currentItems.filter(
            (item) => String(item?.id || "") !== String(recommendationId)
          );

          return {
            ...current,
            items: nextItems,
            count: Math.max(0, safeNumber(current.count, currentItems.length) - 1),
            loaded: true,
          };
        });

        setAcceptedRecommendationsMessage(`${restoredTitle} restored to active recommendations.`);

        if (data?.result?.new_status === "active") {
          setRecommendationSummary((current) =>
            current
              ? {
                  ...current,
                  restored_recommendation_id: recommendationId,
                }
              : current
          );
        }
      } catch (e) {
        console.warn("Accepted recommendation restore failed:", e);
        setAcceptedRecommendationsError(String(e?.message || e));
      } finally {
        setRestoringRecommendationId(null);
      }
    },
    [fetchToken, postJsonWithToken]
  );

  const prepareRecommendationHandoffPreview = useCallback(
    async (recommendation = {}) => {
      const recommendationId = recommendation?.id;
      if (!recommendationId) {
        setAcceptedRecommendationsError("Handoff preview requires a persisted recommendation ID.");
        return;
      }

      setPreparingHandoffRecommendationId(recommendationId);
      setRecommendationHandoffPreview(null);
      setRecommendationHandoffPreviewError("");
      setDraftHandoffResult(null);
      setDraftHandoffError("");
      setRecommendationHandoffPreviewLoading(true);
      setShowRecommendationHandoffPreviewModal(true);

      try {
        const token = await fetchToken();
        const data = await postJsonWithToken(
          token,
          `/recommendations/${recommendationId}/handoff/preview`,
          {}
        );

        setRecommendationHandoffPreview(data || null);
      } catch (e) {
        console.warn("Recommendation handoff preview failed:", e);
        setRecommendationHandoffPreviewError(String(e?.message || e));
      } finally {
        setRecommendationHandoffPreviewLoading(false);
        setPreparingHandoffRecommendationId(null);
      }
    },
    [fetchToken, postJsonWithToken]
  );

  const createDraftHandoffFromPreview = useCallback(
    async () => {
      const previewPayload = recommendationHandoffPreview?.preview || recommendationHandoffPreview || {};
      const recommendationId =
        previewPayload.recommendation_id ||
        recommendationHandoffPreview?.recommendation_id;

      if (!recommendationId) {
        setDraftHandoffError("Draft handoff requires a persisted recommendation ID.");
        return;
      }

      setCreatingDraftHandoff(true);
      setDraftHandoffError("");

      try {
        const token = await fetchToken();
        const data = await postJsonWithToken(
          token,
          `/recommendations/${recommendationId}/handoff/draft`,
          {}
        );

        setDraftHandoffResult(data || null);

        const handoff = data?.handoff || null;
        if (handoff?.id) {
          setDraftHandoffs((current) => {
            const currentItems = Array.isArray(current.items) ? current.items : [];
            const alreadyPresent = currentItems.some(
              (item) => String(item?.id || "") === String(handoff.id)
            );
            const nextItems = alreadyPresent ? currentItems : [handoff, ...currentItems];

            return {
              ...current,
              items: nextItems,
              count: alreadyPresent
                ? safeNumber(current.count, nextItems.length)
                : safeNumber(current.count, currentItems.length) + 1,
              loaded: true,
            };
          });
          setDraftHandoffsMessage(
            data?.execution_status === "draft_existing"
              ? "Draft handoff already exists."
              : "Draft handoff created."
          );
        }
      } catch (e) {
        console.warn("Draft handoff creation failed:", e);
        setDraftHandoffError(String(e?.message || e));
      } finally {
        setCreatingDraftHandoff(false);
      }
    },
    [fetchToken, postJsonWithToken, recommendationHandoffPreview]
  );

  const recommendedNextActions = useMemo(() => {
    const rankedItems = Array.isArray(recommendationSummary?.ranked)
      ? recommendationSummary.ranked
      : [];

    const summaryTopPriorities = Array.isArray(recommendationSummary?.summary?.top_priorities)
      ? recommendationSummary.summary.top_priorities
      : [];

    const legacyTopPriorities = Array.isArray(recommendationSummary?.top_priorities)
      ? recommendationSummary.top_priorities
      : [];

    const items = rankedItems.length
      ? rankedItems
      : summaryTopPriorities.length
      ? summaryTopPriorities
      : legacyTopPriorities;

    const activeItems = items.filter(
      (item) => String(item?.status || "active").trim().toLowerCase() !== "accepted"
    );

    return {
      loaded: Boolean(recommendationSummary),
      candidateCount: safeNumber(recommendationSummary?.candidate_count, 0),
      items: activeItems.slice(0, 3),
      engineVersion: "",
      routesVersion: recommendationSummary?.routes_version || "",
    };
  }, [recommendationSummary]);

  const priorityActions = useMemo(() => {
    const rows = Array.isArray(priorityActionQueue?.actions)
      ? priorityActionQueue.actions
      : [];

    return rows.slice(0, 4).map((action) => {
      const destination =
        action?.destination && typeof action.destination === "object"
          ? action.destination
          : {};

      return {
        actionType: String(action?.action_type || "").trim(),
        actionKey: String(action?.action_key || "").trim(),
        sourceType: String(action?.source_type || "").trim(),
        sourceIdentifiers:
          action?.source_identifiers && typeof action.source_identifiers === "object"
            ? action.source_identifiers
            : {},
        derivationReason: String(action?.derivation_reason || "").trim(),
        title: String(action?.title || "Priority action").trim(),
        detail: String(action?.detail || "Review current authoritative Mission Control state.").trim(),
        cta: String(action?.cta || "Open").trim(),
        mode: String(action?.mode || destination?.mode || "dashboard").trim(),
        params:
          action?.params && typeof action.params === "object"
            ? action.params
            : destination?.params && typeof destination.params === "object"
            ? destination.params
            : {},
      };
    });
  }, [priorityActionQueue]);


  const explainMissionControlPriorityAction = useCallback(
    async (
      action,
      question = "Explain this action.",
      signal
    ) => {
      const cleanedQuestion = String(question || "").trim();
      if (!action || !cleanedQuestion || !tenantId || !userId) {
        return null;
      }

      const actionKey = String(action?.actionKey || "").trim();
      const actionType = String(action?.actionType || "").trim();
      if (!actionKey || !actionType) {
        return null;
      }

      const sameRecord = Boolean(
        contextualExplain.recordType === "priority_action" &&
          contextualExplain.record &&
          String(contextualExplain.record?.actionKey || "") === actionKey
      );

      setContextualExplain((current) => ({
        ...current,
        open: true,
        recordType: "priority_action",
        record: action,
        question: cleanedQuestion,
        card: sameRecord ? current.card : null,
        metadata: sameRecord ? current.metadata : null,
        loading: true,
        error: "",
      }));

      try {
        const token = await fetchToken();
        const sourceIdentifiers =
          action?.sourceIdentifiers &&
          typeof action.sourceIdentifiers === "object"
            ? action.sourceIdentifiers
            : {};

        const response = await postJsonWithToken(
          token,
          "/ai/copilot/ask",
          {
            question: cleanedQuestion,
            scope: "mission_control",
            copilot_context: {
              page: "mission_control",
              surface: "mission_control",
              intelligence_mode: "contextual_explain",
              record_type: "priority_action",
              priority_action_key: actionKey,
              priority_action_type: actionType,
              priority_action_source_type: action?.sourceType || null,
              ...Object.fromEntries(
                Object.entries(sourceIdentifiers).filter(
                  ([, value]) =>
                    value !== null &&
                    value !== undefined &&
                    String(value).trim() !== ""
                )
              ),
            },
          },
          signal
        );

        const card =
          response?.card && typeof response.card === "object"
            ? response.card
            : null;

        if (!card) {
          throw new Error(
            "OASYS Intelligence returned no grounded explanation for this priority action."
          );
        }

        setContextualExplain({
          open: true,
          recordType: "priority_action",
          record: action,
          question: cleanedQuestion,
          card,
          metadata:
            card?.metadata && typeof card.metadata === "object"
              ? card.metadata
              : response?.metadata || null,
          loading: false,
          error: "",
        });

        return card;
      } catch (e) {
        if (e?.name === "AbortError") return null;

        setContextualExplain((current) => ({
          ...current,
          open: true,
          recordType: "priority_action",
          record: action,
          question: cleanedQuestion,
          loading: false,
          error: formatApiErrorMessage(e),
        }));

        return null;
      }
    },
    [
      contextualExplain.record,
      contextualExplain.recordType,
      fetchToken,
      postJsonWithToken,
      tenantId,
      userId,
    ]
  );

  const riskMeta = useMemo(() => {
    if (heroMetrics.operationalScore === null) {
      return { label: "Risk undetermined", tone: "neutral", description: "No framework posture assessment is available. Collected evidence and freshness warnings do not establish security risk." };
    }
    if (
      heroMetrics.operationalScore >= 90 &&
      heroMetrics.openAlerts === 0 &&
      heroMetrics.activeFindings === 0
    ) {
      return {
        label: "Stable • Low Risk",
        tone: "low",
        description:
          "Operational posture is strong. Raw posture remains visible for audit review, and accepted risk is separated instead of hidden.",
      };
    }

    if (heroMetrics.operationalScore >= 75) {
      return {
        label: "Watchlist • Medium Risk",
        tone: "medium",
        description:
          "Operational posture is holding, but active findings, stale evidence, accepted risk, or open alerts still require attention.",
      };
    }

    return {
      label: "Action Required • High Risk",
      tone: "high",
      description:
        "Material active findings or alert volume are dragging operational posture down. Prioritize investigation and remediation.",
    };
  }, [heroMetrics]);

  const sopCenterMetrics = useMemo(() => {
    const pendingReviewCount = safeNumber(sopPendingApproval.count, 0);
    const overdueSops = safeNumber(sopOverdue.count, 0);

    const overdueFootnote =
      overdueSops > 0
        ? `${overdueSops} SOP${overdueSops === 1 ? "" : "s"} overdue/outdated`
        : sopOverdue.loaded
        ? "No overdue SOPs for this tenant"
        : "Overdue SOP data unavailable";

    const pendingReviewFootnote =
      pendingReviewCount > 0
        ? `${pendingReviewCount} SOP revision${
            pendingReviewCount === 1 ? "" : "s"
          } awaiting approval`
        : sopPendingApproval.loaded
        ? "No SOP revisions awaiting approval"
        : "Pending approval data unavailable";

    return {
      overdueSops,
      pendingReviewCount,
      activeFindingSops: null,
      overdueFootnote,
      pendingReviewFootnote,
      summaryFootnote:
        overdueSops > 0
          ? overdueFootnote
          : pendingReviewFootnote,
    };
  }, [sopOverdue, sopPendingApproval]);

  const openMode = useCallback(
    (mode, params = {}) => {
      if (!onModeChange) return;

      scrollMissionControlToTop();

      const missionControlReturnParams = {
        ...params,
        source: params.source || "mission_control",
        sourceMode: params.sourceMode || "dashboard",
        returnMode: params.returnMode || "dashboard",
        returnTo: params.returnTo || "mission_control",
        missionControlReturn: true,
        missionControlNavNonce: String(Date.now()),
      };

      if (mode === "posture") {
        const sectionValue =
          missionControlReturnParams.section ||
          missionControlReturnParams.targetSection ||
          missionControlReturnParams.target_section ||
          "";
        const queueValue =
          missionControlReturnParams.remediationQueue ||
          missionControlReturnParams.remediation_queue ||
          missionControlReturnParams.operationsQueue ||
          missionControlReturnParams.operations_queue ||
          missionControlReturnParams.queue ||
          "";

        const nextParams = {
          ...missionControlReturnParams,
          routeNavNonce: String(Date.now()),
        };

        if (queueValue) {
          nextParams.section = "";
          nextParams.targetSection = "";
          nextParams.target_section = "";
        }

        if (sectionValue && !queueValue) {
          nextParams.remediationQueue = "";
          nextParams.remediation_queue = "";
          nextParams.operationsQueue = "";
          nextParams.operations_queue = "";
          nextParams.queue = "";
        }

        onModeChange(mode, nextParams);
        return;
      }

      onModeChange(mode, missionControlReturnParams);
    },
    [onModeChange, scrollMissionControlToTop]
  );

  const openRecommendationAction = useCallback(
    (recommendation = {}) => {
      setRecommendationFeedbackState({ loadingType: null, message: "", error: "" });
      setSelectedRecommendation(recommendation || null);
    },
    []
  );

  const openSelectedRecommendationReview = useCallback(() => {
    if (!selectedRecommendation) return;

    const target = getRecommendationNavigationTarget(
      selectedRecommendation,
      overview?.framework || undefined
    );

    setSelectedRecommendation(null);
    openMode(target.mode, target.params);
  }, [openMode, overview?.framework, selectedRecommendation]);

  const submitRecommendationFeedback = useCallback(
    async (feedbackType) => {
      if (!selectedRecommendation?.id) {
        setRecommendationFeedbackState({
          loadingType: null,
          message: "",
          error: "Feedback requires a persisted recommendation record.",
        });
        return;
      }

      setRecommendationFeedbackState({
        loadingType: feedbackType,
        message: "",
        error: "",
      });

      try {
        const token = await fetchToken();
        const result = await postJsonWithToken(
          token,
          `/recommendations/${selectedRecommendation.id}/feedback`,
          {
            feedback_type: feedbackType,
            comment: `Operator marked recommendation as ${feedbackType}.`,
            metadata: {
              surface: selectedRecommendation.surface || "mission_control",
              source: "mission_control_recommendation_modal",
            },
          }
        );

        const feedbackResult = result?.result || {};
        const feedbackLabel =
          feedbackType === "useful"
            ? "Feedback saved: useful."
            : feedbackType === "not_useful"
            ? "Feedback saved: not useful."
            : feedbackType === "accepted"
            ? ""
            : "Recommendation dismissed.";

        setRecommendationFeedbackState({
          loadingType: null,
          message: feedbackLabel,
          error: "",
        });

        if (feedbackType === "dismissed" || feedbackType === "not_relevant") {
          setRecommendationSummary((current) => {
            if (!current) return current;

            const removeById = (items) =>
              Array.isArray(items)
                ? items.filter((item) => String(item?.id || "") !== String(selectedRecommendation.id))
                : items;

            return {
              ...current,
              ranked: removeById(current.ranked),
              top_priorities: removeById(current.top_priorities),
              summary: current.summary
                ? {
                    ...current.summary,
                    top_priorities: removeById(current.summary.top_priorities),
                  }
                : current.summary,
            };
          });

          setSelectedRecommendation(null);
        } else {
          setSelectedRecommendation((current) =>
            current
              ? {
                  ...current,
                  status: feedbackType === "accepted" ? "accepted" : current.status,
                  feedback_result: feedbackResult,
                }
              : current
          );

          if (feedbackType === "accepted") {
            setRecommendationSummary((current) => {
              if (!current) return current;

              const removeAccepted = (items) =>
                Array.isArray(items)
                  ? items.filter((item) => String(item?.id || "") !== String(selectedRecommendation.id))
                  : items;

              return {
                ...current,
                ranked: removeAccepted(current.ranked),
                top_priorities: removeAccepted(current.top_priorities),
                summary: current.summary
                  ? {
                      ...current.summary,
                      top_priorities: removeAccepted(current.summary.top_priorities),
                    }
                  : current.summary,
              };
            });
          }
        }
      } catch (e) {
        setRecommendationFeedbackState({
          loadingType: null,
          message: "",
          error: String(e?.message || e || "Recommendation feedback failed."),
        });
      }
    },
    [fetchToken, postJsonWithToken, selectedRecommendation]
  );

  if (loading || (!err && (!frameworkScope || frameworkScope.tenantId !== tenantId))) {
    return (
      <div className={`oasys-dashboard ${themeMode === "dark" ? "dark" : ""}`}>
        <div className="dash-shell">

          <div className="dash-header">
            <div>
              <div className="dash-title">Mission Control</div>
            </div>
          </div>
          <div className="card" style={{ padding: 20, marginTop: 16 }}>
            Loading Mission Control…
          </div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className={`oasys-dashboard ${themeMode === "dark" ? "dark" : ""}`}>
        <div className="dash-shell">

          <div className="dash-header">
            <div>
              <div className="dash-title">Mission Control</div>
            </div>
          </div>
          <div className="card" style={{ padding: 20, marginTop: 16 }}>
            <div style={{ color: "#ff7b7b" }}>Failed to load Mission Control: {err}</div>
          </div>
        </div>
      </div>
    );
  }

  const captureFeatureVisible = process.env.REACT_APP_OPERATIONAL_CAPTURE_UI_ENABLED === "1";

  return (
    <div className={`oasys-dashboard ${themeMode === "dark" ? "dark" : ""}`}>
      <div className="dash-shell">
        <MissionControlHeader
          framework={frameworkFilter}
          captureProps={captureFeatureVisible ? { label: "Capture Evidence Now", apiBaseUrl, getAccessToken: fetchToken, tenantId, userId, discoverConnector: true, onCompleted: handleCaptureCompleted } : null}
          dataAsOf={heroMetrics.dataAsOf}
          warningCount={panelWarnings.length}
          refreshing={refreshingView}
          refreshMessage={refreshMessage}
          refreshError={refreshError}
          onRefresh={refreshMissionControlView}
          onOpenPosture={() =>
            openMode("posture", {
              framework:
                frameworkFilter === "all" ? undefined : frameworkFilter,
            })
          }
        />

        <div className="mission-control-kpi-strip" aria-label="Executive posture metrics">
          <ExecutivePostureMetrics
            metrics={heroMetrics}
            formatSignedNumber={formatSignedNumber}
            onOpenScoreDetails={() => setShowScoreDetailsModal(true)}
            onOpenTopRisks={() =>
              openMode("posture", {
                framework: overview?.framework || undefined,
                section: "top_risks",
              })
            }
            onOpenAcceptedRisk={() =>
              openMode("posture", {
                framework: overview?.framework || undefined,
                remediationQueue: "approved_exceptions",
              })
            }
            onOpenClosedRecords={() =>
              openMode("posture", {
                framework: overview?.framework || undefined,
                remediationQueue: "closed",
              })
            }
            onOpenAlerts={() => openMode("alerts")}
            onOpenStaleControls={() =>
              openMode("posture", {
                framework: overview?.framework || undefined,
                section: "stale",
              })
            }
          />
        </div>

        <MissionControlIntelligencePanel
          question={missionControlIntelligence.question}
          card={missionControlIntelligence.card}
          metadata={missionControlIntelligence.metadata}
          loading={missionControlIntelligence.loading}
          error={missionControlIntelligence.error}
          onAsk={askMissionControlIntelligence}
        />

        {panelWarnings.length > 0 && (
          <div
            className="card"
            style={{
              padding: "10px 14px",
              marginBottom: 16,
              display: "grid",
              gap: 6,
            }}
          >
            <div
              style={{
                color: "var(--warn)",
                fontWeight: 700,
                fontSize: 13,
                lineHeight: 1.3,
              }}
            >
              Mission Control loaded with partial data
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                color: "var(--muted)",
                fontSize: 12,
                lineHeight: 1.4,
              }}
            >
              {panelWarnings.map((warning, index) => (
                <span
                  key={`${warning}-${index}`}
                  style={{
                    padding: "4px 8px",
                    borderRadius: 999,
                    border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                    background: "color-mix(in srgb, var(--card), transparent 12%)",
                  }}
                >
                  {warning}
                </span>
              ))}
            </div>
          </div>
        )}

        <section className="hero-grid">
          <div className="hero-primary-stack">
            <ExecutivePosturePanel
              metrics={heroMetrics}
              risk={riskMeta}
              frameworks={frameworkScope?.tenantId === tenantId ? frameworkScope.frameworks : []}
              selectedFramework={frameworkFilter}
              onFrameworkChange={setFrameworkFilter}
              formatSignedNumber={formatSignedNumber}
              formatRelativeTime={formatRelativeTime}
              formatTimestamp={formatTimestamp}
              refreshing={refreshingView}
              refreshMessage={refreshMessage}
              refreshError={refreshError}
              onRefresh={refreshMissionControlView}
              onOpenScoreDetails={() => setShowScoreDetailsModal(true)}
              onOpenTopRisks={() =>
                openMode("posture", {
                  framework: overview?.framework || undefined,
                  section: "top_risks",
                })
              }
              onOpenAcceptedRisk={() =>
                openMode("posture", {
                  framework: overview?.framework || undefined,
                  remediationQueue: "approved_exceptions",
                })
              }
              onOpenClosedRecords={() =>
                openMode("posture", {
                  framework: overview?.framework || undefined,
                  remediationQueue: "closed",
                })
              }
              onOpenAlerts={() => openMode("alerts")}
              onOpenStaleControls={() =>
                openMode("posture", {
                  framework: overview?.framework || undefined,
                  section: "stale",
                })
              }
            />
          </div>

          <ControlsPreviewPanel
            rows={controlsPreview.rows}
            loading={controlsPreview.loading}
            error={controlsPreview.error}
            framework={frameworkFilter}
            onOpenAll={() => openMode("posture", { framework: frameworkFilter })}
          />

          <div className="hero-side">
            <FrameworkHealthPanel
              rows={displayedFrameworkRows}
              onOpenPosture={() => openMode("posture")}
              onOpenFramework={(frameworkName) =>
                openMode("posture", { framework: frameworkName })
              }
            />

            <div className="card hero-open-items mission-recommendations-preview">
              <div className="card-header">
                <div>
                  <div className="card-title">Recommended Next Actions</div>
                  <div className="connector-health-subtitle">
                    Advisory guidance · opens review pages only
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    alignItems: "flex-end",
                  }}
                >
                  <span
                    className={`risk-pill risk-${
                      recommendedNextActions.items[0]
                        ? recommendationPriorityTone(recommendedNextActions.items[0].priority)
                        : "low"
                    }`}
                  >
                    {recommendationLoading
                      ? "Loading"
                      : recommendedNextActions.items[0]
                      ? formatRecommendationPriority(recommendedNextActions.items[0].priority)
                      : "Clear"}
                  </span>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xxs mission-action-pill"
                      onClick={openDraftHandoffsModal}
                      style={{
                        whiteSpace: "nowrap",
                        minWidth: 116,
                        justifyContent: "center",
                        padding: "6px 10px",
                        fontSize: 12,
                        lineHeight: 1.1,
                      }}
                    >
                      View drafts
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xxs mission-action-pill"
                      onClick={openConvertedHandoffsModal}
                      style={{
                        whiteSpace: "nowrap",
                        minWidth: 116,
                        justifyContent: "center",
                        padding: "6px 10px",
                        fontSize: 12,
                        lineHeight: 1.1,
                      }}
                    >
                      View converted
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xxs mission-action-pill"
                      onClick={openAcceptedRecommendationsModal}
                      style={{
                        whiteSpace: "nowrap",
                        minWidth: 116,
                        justifyContent: "center",
                        padding: "6px 10px",
                        fontSize: 12,
                        lineHeight: 1.1,
                      }}
                    >
                      View accepted
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xxs mission-action-pill"
                      onClick={openDismissedRecommendationsModal}
                      style={{
                        whiteSpace: "nowrap",
                        minWidth: 116,
                        justifyContent: "center",
                        padding: "6px 10px",
                        fontSize: 12,
                        lineHeight: 1.1,
                      }}
                    >
                      View dismissed
                    </button>
                  </div>
                </div>
              </div>

              <div className="card-body" style={{ display: "grid", gap: 10 }}>
                {recommendationLoading ? (
                  <div className="muted">Building ranked recommendations…</div>
                ) : recommendationError ? (
                  <div className="muted">Recommendation engine unavailable: {recommendationError}</div>
                ) : recommendedNextActions.items.length === 0 ? (
                  <div className="muted">No recommendations generated from current Mission Control context.</div>
                ) : (
                  recommendedNextActions.items.slice(0, 3).map((recommendation, index) => (
                    <button
                      key={`${recommendation.title || "recommendation"}-${index}`}
                      type="button"
                      className="open-item-pill"
                      onClick={() => openRecommendationAction(recommendation)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        borderRadius: 14,
                        color: "var(--oasys-color-text-primary, var(--text))",
                      }}
                      title="Open recommendation details"
                    >
                      <div style={{ display: "grid", gap: 8, width: "100%" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            alignItems: "flex-start",
                          }}
                        >
                          <div style={{ display: "grid", gap: 4 }}>
                            <div style={{ fontWeight: 800, color: "var(--oasys-color-text-primary, var(--text))" }}>
                              {recommendation.title || "Recommendation"}
                            </div>
                            <div className="mission-recommendation__summary">
                              {recommendation.summary ||
                                recommendation.reasoning_summary ||
                                recommendation.suggested_action ||
                                "Review recommended action."}
                            </div>
                          </div>

                          <span
                            className={`risk-pill risk-${recommendationPriorityTone(
                              recommendation.priority
                            )}`}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            {formatRecommendationPriority(recommendation.priority)}
                          </span>
                        </div>

                        <div className="mission-recommendation__meta">
                          <span>{String(recommendation.category || "recommendation").replace(/_/g, " ")}</span>
                          {recommendation.total_score ? <span>Score: {recommendation.total_score}</span> : null}
                          {recommendation.confidence_level ? (
                            <span>Confidence: {recommendation.confidence_level}</span>
                          ) : null}
                          {recommendation.rank ? <span>Rank: {recommendation.rank}</span> : null}
                        </div>

                        {recommendation.suggested_action ? (
                          <div className="mission-recommendation__action">
                            {recommendation.suggested_action}
                          </div>
                        ) : null}
                      </div>
                    </button>
                  ))
                )}

                <div style={{ color: "var(--muted)", fontSize: 11, lineHeight: 1.4 }}>
                  {recommendedNextActions.loaded
                    ? `Showing ${Math.min(3, recommendedNextActions.items.length)} of ${
                        recommendedNextActions.items.length
                      } ranked recommendation${
                        recommendedNextActions.items.length === 1 ? "" : "s"
                      } · ${recommendedNextActions.candidateCount} candidate${
                        recommendedNextActions.candidateCount === 1 ? "" : "s"
                      } evaluated`
                    : "Recommendations are generated from current Mission Control context."}
                </div>
              </div>
            </div>

          </div>
        </section>

        <section className="mission-utility-strips mission-utility-strips-full" aria-label="Mission Control operational context">
          <OperationalSignals
            validation={validationMetrics}
            connector={connectorHealthMetrics}
            identity={identityRiskMetrics}
            codeEdge={connectorEvidenceMetrics}
            workflow={workflowHealthMetrics}
            formatConnectorProvider={formatConnectorProvider}
            formatDurationSeconds={formatDurationSeconds}
            onExplainValidation={() =>
              explainMissionControlValidation(
                validationMetrics.latestFailedValidation
              )
            }
            onOpenValidation={() =>
              openMode("validation", {
                framework:
                  validationMetrics.latestFailedValidation?.framework ||
                  overview?.framework ||
                  undefined,
                section: "failed",
              })
            }
            onExplainConnector={() =>
              explainMissionControlConnector(
                connectorHealthMetrics.topIssue
              )
            }
            onOpenConnectors={() =>
              openMode("settings", { settingsSection: "connectors" })
            }
            onOpenIdentity={() =>
              openMode("posture", {
                framework: overview?.framework || undefined,
                section: "top_risks",
              })
            }
            onOpenCodeEdge={() =>
              openMode("posture", {
                framework: overview?.framework || undefined,
                section: "top_risks",
              })
            }
            onOpenWorkflow={() =>
              openMode("workflows", { section: "workflow_health" })
            }
          />

            <div className="card mission-strip mission-filter-strip">
              <div className="mission-strip-main">
                <div className="mission-strip-heading">
                  <div>
                    <div className="card-title">Mission Filters</div>
                    <div className="connector-health-subtitle">
                      Scope the executive signal without leaving Mission Control.
                    </div>
                  </div>
                </div>

                <div className="mission-filter-strip-controls">
                  <div className="mission-filter-control">
                    <div className="filter-label">Framework</div>
                    <select
                      className="filter-select"
                      value={frameworkFilter}
                      onChange={(e) => setFrameworkFilter(e.target.value)}
                    >
                      {(frameworkScope?.frameworks || []).map((fw) => (
                        <option key={fw} value={fw}>{fw}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mission-filter-control mission-filter-control-chips">
                    <div className="filter-label">Alerts shown</div>
                    <div className="filter-chips">
                      {[5, 8, 12].map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={`chip sm ${alertsLimit === value ? "active" : ""}`}
                          onClick={() => setAlertsLimit(value)}
                        >
                          Top {value}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mission-filter-note">
                    Live posture, freshness, and alerting data.
                  </div>
                </div>
              </div>
            </div>

        </section>



        <PriorityAttentionPanel
          actions={priorityActions}
          sopMetrics={sopCenterMetrics}
          onExplainAction={(action) =>
            explainMissionControlPriorityAction(action)
          }
          onOpenAction={(action) =>
            openMode(action.mode, action.params || {})
          }
          onOpenSop={(filter) =>
            openMode("sop", filter ? { filter } : {})
          }
        />

        <section
          className="mission-work-content"
          aria-label="Mission Control risks, alerts, and freshness"
        >
            <section className="section-card mission-work-panel mission-work-panel--risks">
              <div className="section-header mission-work-panel__header">
                <div>
                  <div className="section-title">Top Risks</div>
                  <div className="section-subtitle">
                    Highest-weighted control risks from current framework scoring.
                  </div>
                </div>

                <button
                  className="btn btn-ghost btn-sm mission-work-panel__header-action"
                  onClick={() => openMode("posture")}
                >
                  Review posture
                </button>
              </div>

              <div className="tasks-list mission-work-list">
                {topRisks.length === 0 ? (
                  <div className="mission-work-empty">
                    No live top-risk records available.
                  </div>
                ) : (
                  topRisks.map((risk, idx) => (
                    <article
                      key={`${risk.framework}-${risk.control_id}-${risk.rule_id}-${idx}`}
                      className="task-row mission-work-row mission-work-row--risk"
                    >
                      <div className="task-main mission-work-row__content">
                        <div className="mission-work-row__title-line">
                          <div className="task-title">
                            {risk.control_id} · {risk.rule_id}
                          </div>
                          <span className="task-severity task-high">Risk</span>
                        </div>

                        <div className="task-meta mission-work-row__meta">
                          <span>{risk.framework}</span>
                          <span>Severity weight: {risk.severity_weight}</span>
                          <span>Score: {safeNumber(risk.score).toFixed(2)}</span>
                        </div>

                        <p className="mission-work-row__description">
                          {risk.reason_summary || "No reason summary available."}
                        </p>

                        {risk.remediation_hint ? (
                          <p className="mission-work-row__remediation">
                            <strong>Recommended response</strong>
                            <span>{risk.remediation_hint}</span>
                          </p>
                        ) : null}
                      </div>

                      <div className="task-right mission-work-row__actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs"
                          onClick={() => explainMissionControlRisk(risk)}
                        >
                          Explain
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs"
                          onClick={() =>
                            openMode("posture", { framework: risk.framework })
                          }
                        >
                          Investigate
                        </button>
                      </div>
                    </article>
                  ))
                )}
              </div>
            </section>

            <section className="section-card mission-work-panel mission-work-panel--alerts">
              <div className="section-header mission-work-panel__header">
                <div>
                  <div className="section-title">Active Alerts</div>
                  <div className="section-subtitle">
                    Operational alerting proves OASYS is monitoring and acting, not just reporting.
                  </div>
                </div>

                <button
                  className="btn btn-ghost btn-sm mission-work-panel__header-action"
                  onClick={() => openMode("alerts")}
                >
                  Open alerts
                </button>
              </div>

              <div className="tasks-list mission-work-list">
                {recentAlerts.length === 0 ? (
                  <div className="mission-work-empty">
                    Recent alerts are currently unavailable or still loading from the backend.
                  </div>
                ) : (
                  recentAlerts.map((alert) => (
                    <article
                      key={alert.id}
                      className="task-row mission-work-row mission-work-row--alert"
                    >
                      <div className="task-main mission-work-row__content">
                        <div className="mission-work-row__title-line">
                          <div className="task-title">{alert.title}</div>
                          <div className="mission-work-row__status-group">
                            <SeverityPill value={alert.severity} />
                            <StatusPill value={alert.status} />
                          </div>
                        </div>

                        <div className="task-meta mission-work-row__meta">
                          <span>{alert.alert_type}</span>
                          {alert.framework ? <span>{alert.framework}</span> : null}
                          {alert.resource_id ? <span>{alert.resource_id}</span> : null}
                          <span>{formatRelativeTime(alert.created_at)}</span>
                        </div>

                        <p className="mission-work-row__description">
                          {alert.message}
                        </p>
                      </div>

                      <div className="task-right mission-work-row__actions mission-work-row__actions--stacked">
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={() => openMode("alerts")}
                        >
                          Acknowledge
                        </button>
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={() => openMode("alerts")}
                        >
                          Snooze
                        </button>
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={() => openMode("alerts")}
                        >
                          Open details
                        </button>
                      </div>
                    </article>
                  ))
                )}
              </div>
            </section>

            <section className="section-card mission-work-panel mission-work-panel--freshness">
              <div className="section-header mission-work-panel__header">
                <div>
                  <div className="section-title">Stale Controls / Freshness</div>
                  <div className="section-subtitle">
                    Freshness debt is first-class. These controls need recertification or evidence refresh.
                  </div>
                </div>

                <button
                  className="btn btn-ghost btn-sm mission-work-panel__header-action"
                  onClick={() => openMode("drift")}
                >
                  Open drift &amp; state
                </button>
              </div>

              <div className="tasks-list mission-work-list">
                {staleControls.length === 0 ? (
                  <div className="mission-work-empty">
                    Stale controls are currently unavailable or still loading from the backend.
                  </div>
                ) : (
                  staleControls.map((control, idx) => (
                    <article
                      key={`${control.framework}-${control.control_id}-${control.rule_id}-${idx}`}
                      className="task-row mission-work-row mission-work-row--freshness"
                    >
                      <div className="task-main mission-work-row__content">
                        <div className="mission-work-row__title-line">
                          <div className="task-title">
                            {control.control_id} · {control.rule_id}
                          </div>
                          <span className="task-severity task-medium">
                            {control.freshness_status}
                          </span>
                        </div>

                        <div className="task-meta mission-work-row__meta">
                          <span>{control.framework}</span>
                          <span>SLA: {control.sla_hours}h</span>
                          <span>Last seen: {formatTimestamp(control.last_seen_at)}</span>
                        </div>
                      </div>

                      <div className="task-right mission-work-row__actions">
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={() =>
                            openMode("posture", { framework: control.framework })
                          }
                        >
                          Inspect
                        </button>
                      </div>
                    </article>
                  ))
                )}
              </div>
            </section>
        </section>

        <RecommendationDetailModal
          recommendation={selectedRecommendation}
          feedbackState={recommendationFeedbackState}
          onClose={() => {
            setSelectedRecommendation(null);
            setRecommendationFeedbackState({ loadingType: null, message: "", error: "" });
          }}
          onOpenReviewPage={openSelectedRecommendationReview}
          onSubmitFeedback={submitRecommendationFeedback}
        />

        <AcceptedRecommendationsModal
          open={showAcceptedRecommendationsModal}
          items={acceptedRecommendations.items}
          count={acceptedRecommendations.count}
          loading={acceptedRecommendationsLoading}
          error={acceptedRecommendationsError}
          message={acceptedRecommendationsMessage}
          restoringId={restoringRecommendationId}
          preparingHandoffId={preparingHandoffRecommendationId}
          onClose={() => setShowAcceptedRecommendationsModal(false)}
          onRefresh={loadAcceptedRecommendations}
          onRestore={restoreAcceptedRecommendation}
          onPrepareHandoff={prepareRecommendationHandoffPreview}
          onViewDraftHandoffs={openDraftHandoffsFromHandoffPreview}
        />

        <RecommendationHandoffPreviewModal
          open={showRecommendationHandoffPreviewModal}
          preview={recommendationHandoffPreview}
          loading={recommendationHandoffPreviewLoading}
          error={recommendationHandoffPreviewError}
          creatingDraftHandoff={creatingDraftHandoff}
          draftHandoffResult={draftHandoffResult}
          draftHandoffError={draftHandoffError}
          onCreateDraftHandoff={createDraftHandoffFromPreview}
          onViewDraftHandoffs={openDraftHandoffsModal}
          onClose={() => {
            setShowRecommendationHandoffPreviewModal(false);
            setRecommendationHandoffPreviewError("");
            setDraftHandoffError("");
          }}
        />

        <DraftRecommendationHandoffsModal
          open={showDraftHandoffsModal}
          items={draftHandoffs.items}
          count={draftHandoffs.count}
          loading={draftHandoffsLoading}
          error={draftHandoffsError}
          message={draftHandoffsMessage}
          cancellingId={cancellingDraftHandoffId}
          previewingConvertId={previewingConvertHandoffId}
          onClose={() => setShowDraftHandoffsModal(false)}
          onRefresh={loadDraftRecommendationHandoffs}
          onCancel={cancelDraftRecommendationHandoff}
          onPreviewConversion={previewDraftHandoffConversion}
        />

        <ConvertedRecommendationHandoffsModal
          open={showConvertedHandoffsModal}
          items={convertedHandoffs.items}
          count={convertedHandoffs.count}
          loading={convertedHandoffsLoading}
          error={convertedHandoffsError}
          message={convertedHandoffsMessage}
          onRefresh={loadConvertedRecommendationHandoffs}
          onClose={() => {
            setShowConvertedHandoffsModal(false);
            setConvertedHandoffsError("");
          }}
        />

        <RecommendationHandoffConvertPreviewModal
          open={showHandoffConvertPreviewModal}
          preview={handoffConvertPreview}
          loading={handoffConvertPreviewLoading}
          error={handoffConvertPreviewError}
          confirming={confirmingHandoffConversion}
          conversionResult={handoffConversionResult}
          conversionError={handoffConversionError}
          onConfirmConversion={confirmDraftHandoffConversion}
          onClose={() => {
            setShowHandoffConvertPreviewModal(false);
            setHandoffConvertPreviewError("");
            setHandoffConversionError("");
          }}
        />

        <DismissedRecommendationsModal
          open={showDismissedRecommendationsModal}
          items={dismissedRecommendations.items}
          count={dismissedRecommendations.count}
          loading={dismissedRecommendationsLoading}
          error={dismissedRecommendationsError}
          message={dismissedRecommendationsMessage}
          restoringId={restoringRecommendationId}
          onClose={() => setShowDismissedRecommendationsModal(false)}
          onRefresh={loadDismissedRecommendations}
          onRestore={restoreDismissedRecommendation}
        />

        <ContextualExplainDrawer
          open={contextualExplain.open}
          record={contextualExplain.record}
          recordLabel={
            contextualExplain.record
              ? contextualExplain.recordType === "validation"
                ? `${formatConnectorEvidenceRule(
                    contextualExplain.record.rule_id
                  )} validation${
                    contextualExplain.record.resource_id
                      ? ` · ${contextualExplain.record.resource_id}`
                      : ""
                  }`
                : contextualExplain.recordType === "connector"
                ? `${formatConnectorProvider(
                    contextualExplain.record.provider
                  )} connector${
                    contextualExplain.record.operational_health
                      ? ` · ${String(
                          contextualExplain.record.operational_health
                        ).replace(/_/g, " ")}`
                      : ""
                  }`
                : contextualExplain.recordType === "priority_action"
                ? contextualExplain.record.title || "Priority action"
                : `${formatConnectorEvidenceRule(
                    contextualExplain.record.rule_id
                  )}${
                    contextualExplain.record.resource_id ||
                    contextualExplain.record.resource
                      ? ` · ${
                          contextualExplain.record.resource_id ||
                          contextualExplain.record.resource
                        }`
                      : ""
                  }`
              : ""
          }
          question={contextualExplain.question}
          card={contextualExplain.card}
          metadata={contextualExplain.metadata}
          loading={contextualExplain.loading}
          error={contextualExplain.error}
          themeMode={themeMode}
          prompts={
            contextualExplain.recordType === "validation"
              ? [
                  "Explain this validation.",
                  "Why did it fail?",
                  "What does this block?",
                  "What should I verify next?",
                ]
              : contextualExplain.recordType === "connector"
              ? [
                  "Explain this connector.",
                  "Why does it need attention?",
                  "What does this affect?",
                  "What should I check next?",
                ]
              : contextualExplain.recordType === "priority_action"
              ? [
                  "Explain this action.",
                  "Why is this recommended?",
                  "What is driving this?",
                  "Where should I go next?",
                ]
              : undefined
          }
          onAsk={(question) =>
            contextualExplain.recordType === "validation"
              ? explainMissionControlValidation(
                  contextualExplain.record,
                  question
                )
              : contextualExplain.recordType === "connector"
              ? explainMissionControlConnector(
                  contextualExplain.record,
                  question
                )
              : contextualExplain.recordType === "priority_action"
              ? explainMissionControlPriorityAction(
                  contextualExplain.record,
                  question
                )
              : explainMissionControlRisk(
                  contextualExplain.record,
                  question
                )
          }
          onClose={closeContextualExplain}
        />

        {showScoreDetailsModal ? (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Score details"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1000,
              background: "rgba(0, 0, 0, 0.64)",
              display: "grid",
              placeItems: "center",
              padding: 20,
            }}
            onClick={() => setShowScoreDetailsModal(false)}
          >
            <div
              className="card"
              style={{
                width: "min(1024px, calc(100vw - 40px))",
                maxHeight: "calc(100vh - 56px)",
                overflowY: "auto",
                boxShadow: "0 24px 80px rgba(0,0,0,.45)",
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="card-header">
                <div>
                  <div className="dash-eyebrow">OASYS · Score Details</div>
                  <div className="card-title">Raw Score vs Operational Score</div>
                  <p className="dash-subtitle" style={{ margin: "8px 0 0" }}>
                    Raw score preserves the auditor-safe source posture. Operational score
                    separates approved, evidence-backed accepted risk from active unresolved work.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowScoreDetailsModal(false)}
                >
                  Close
                </button>
              </div>

              <div className="card-body" style={{ display: "grid", gap: 24 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 18,
                  }}
                >
                  <MetricCard
                    label="Raw Score"
                    value={heroMetrics.rawScore === null ? "Not assessed" : `${heroMetrics.rawScore.toFixed(2)}%`}
                    footnote="includes all findings"
                    tone={heroMetrics.rawScore === null ? "default" : heroMetrics.rawScore >= 90 ? "ok" : heroMetrics.rawScore >= 75 ? "warn" : "danger"}
                  />
                  <MetricCard
                    label="Operational Score"
                    value={heroMetrics.operationalScore === null ? "Not assessed" : `${heroMetrics.operationalScore.toFixed(2)}%`}
                    footnote="accepted-risk findings separated from active burden"
                    tone={heroMetrics.operationalScore === null ? "default" : heroMetrics.operationalScore >= 90 ? "ok" : heroMetrics.operationalScore >= 75 ? "warn" : "danger"}
                  />
                  <MetricCard
                    label="Raw Delta"
                    value={formatSignedNumber(heroMetrics.rawScoreDelta)}
                    footnote={
                      heroMetrics.previousRawScore === null
                        ? "no previous raw score"
                        : `previous raw ${heroMetrics.previousRawScore.toFixed(2)}%`
                    }
                    tone={
                      heroMetrics.rawScoreDelta > 0
                        ? "ok"
                        : heroMetrics.rawScoreDelta < 0
                        ? "danger"
                        : "default"
                    }
                  />
                  <MetricCard
                    label="Operational Delta"
                    value={formatSignedNumber(heroMetrics.operationalScoreDelta)}
                    footnote={
                      heroMetrics.previousOperationalScore === null
                        ? "no previous operational score"
                        : `previous operational ${heroMetrics.previousOperationalScore.toFixed(2)}%`
                    }
                    tone={
                      heroMetrics.operationalScoreDelta > 0
                        ? "ok"
                        : heroMetrics.operationalScoreDelta < 0
                        ? "danger"
                        : "default"
                    }
                  />
                  <MetricCard
                    label="Raw Fail Findings"
                    value={heroMetrics.rawFailFindings}
                    footnote="from source posture"
                    tone={heroMetrics.rawFailFindings > 0 ? "danger" : "ok"}
                  />
                  <MetricCard
                    label="Active Findings"
                    value={heroMetrics.activeFindings}
                    footnote="unresolved findings"
                    tone={heroMetrics.activeFindings > 0 ? "danger" : "ok"}
                  />
                  <MetricCard
                    label="Accepted Risk"
                    value={heroMetrics.acceptedRiskFindings}
                    footnote="approved exceptions"
                    tone={heroMetrics.acceptedRiskFindings > 0 ? "warn" : "ok"}
                  />
                  <MetricCard
                    label="Raw Nonpassing"
                    value={heroMetrics.rawNonpassingFindings}
                    footnote="fail + error + unknown"
                    tone={heroMetrics.rawNonpassingFindings > 0 ? "warn" : "ok"}
                  />
                </div>

                <div
                  style={{
                    border: "1px solid color-mix(in srgb, var(--brand), transparent 70%)",
                    borderRadius: 18,
                    padding: 18,
                    background: "color-mix(in srgb, var(--brand), transparent 92%)",
                    display: "grid",
                    gap: 12,
                  }}
                >
                  <div style={{ fontWeight: 800 }}>Audit interpretation</div>
                  <div style={{ color: "var(--text)", lineHeight: 1.6 }}>
                    Raw Score is the official source posture and includes every detected finding.
                    Operational Score is the active-work posture after separating approved,
                    evidence-backed accepted risk. Accepted risk is not hidden or treated as a pass;
                    it remains visible, documented, reviewable, and time-bound.
                  </div>
                  <div style={{ color: "var(--muted)", lineHeight: 1.6 }}>
                    {heroMetrics.scoreAdjustmentReason}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() =>
                      openMode("posture", {
                        framework: overview?.framework || undefined,
                        section: "top_risks",
                      })
                    }
                  >
                    Open Top Risks
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() =>
                      openMode("posture", {
                        framework: overview?.framework || undefined,
                        remediationQueue: "approved_exceptions",
                      })
                    }
                  >
                    Review Accepted Risks
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setShowScoreDetailsModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

      </div>
    </div>
  );
}
